import React, { useState, useEffect, useCallback, useRef } from 'react';

const API = 'http://localhost:3003';

// ─── Full 50-state + City/County Hierarchy ───────────────────────────────────
// States with detailed county/city data show cascading dropdowns.
// States without detailed data still appear in the list; county/city show "Any".
const GEO_HIERARCHY = {
    'New Jersey': {
        'Essex County': ['Newark', 'East Orange', 'Montclair', 'Bloomfield', 'Maplewood', 'West Orange', 'Livingston'],
        'Hudson County': ['Jersey City', 'Hoboken', 'Bayonne', 'Union City', 'Weehawken', 'North Bergen', 'Secaucus'],
        'Passaic County': ['Paterson', 'Clifton', 'Passaic', 'Wayne', 'Pompton Lakes', 'Little Falls'],
        'Union County': ['Elizabeth', 'Union', 'Westfield', 'Plainfield', 'Summit', 'Cranford', 'Linden'],
        'Middlesex County': ['Edison', 'Woodbridge', 'New Brunswick', 'Piscataway', 'Old Bridge', 'South Brunswick'],
        'Ocean County': ['Toms River', 'Lakewood', 'Brick Township', 'Jackson', 'Manchester'],
        'Mercer County': ['Trenton', 'Hamilton', 'Princeton', 'West Windsor', 'Ewing'],
        'Camden County': ['Camden', 'Cherry Hill', 'Haddonfield', 'Voorhees', 'Collingswood'],
        'Bergen County': ['Hackensack', 'Paramus', 'Fort Lee', 'Teaneck', 'Englewood', 'Fair Lawn', 'Ridgewood'],
        'Morris County': ['Morristown', 'Parsippany', 'Rockaway', 'Denville', 'Dover', 'Randolph'],
        'Monmouth County': ['Freehold', 'Red Bank', 'Long Branch', 'Asbury Park', 'Tinton Falls'],
        'Somerset County': ['Somerville', 'Bridgewater', 'Bernardsville', 'Warren', 'Basking Ridge'],
        'Burlington County': ['Burlington', 'Moorestown', 'Mount Holly', 'Medford', 'Evesham'],
        'Gloucester County': ['Woodbury', 'Deptford', 'Washington Township', 'Monroe Township'],
        'Atlantic County': ['Atlantic City', 'Egg Harbor Township', 'Galloway', 'Hammonton'],
    },
    'New York': {
        'Manhattan': ['Midtown', 'Downtown', 'Upper East Side', 'Financial District', 'Tribeca', 'SoHo'],
        'Brooklyn': ['Downtown Brooklyn', 'Bay Ridge', 'Williamsburg', 'Flatbush', 'Crown Heights', 'Park Slope'],
        'Queens': ['Flushing', 'Astoria', 'Jamaica', 'Forest Hills', 'Long Island City', 'Bayside'],
        'The Bronx': ['Fordham', 'Riverdale', 'Co-op City', 'Tremont', 'Pelham Bay'],
        'Long Island': ['Garden City', 'Mineola', 'Hicksville', 'Uniondale', 'Hempstead', 'Great Neck', 'Melville'],
        'Westchester County': ['White Plains', 'Yonkers', 'Tarrytown', 'Scarsdale', 'New Rochelle', 'Mount Vernon'],
        'Buffalo / WNY': ['Buffalo', 'Niagara Falls', 'Cheektowaga', 'Amherst', 'Tonawanda'],
        'Upstate NY': ['Albany', 'Syracuse', 'Rochester', 'Utica', 'Rome'],
    },
    'Pennsylvania': {
        'Philadelphia County': ['Philadelphia', 'Center City', 'South Philly', 'Northeast Philly'],
        'Montgomery County': ['Norristown', 'King of Prussia', 'Lansdale', 'Abington', 'Jenkintown'],
        'Delaware County': ['Media', 'Chester', 'Springfield', 'Upper Darby'],
        'Bucks County': ['Doylestown', 'Langhorne', 'Newtown', 'Levittown'],
        'Allegheny County': ['Pittsburgh', 'Mount Lebanon', 'Bethel Park', 'Monroeville', 'Penn Hills'],
        'Lancaster County': ['Lancaster', 'Lititz', 'Ephrata', 'Manheim'],
    },
    'Connecticut': {
        'Fairfield County': ['Bridgeport', 'Stamford', 'Norwalk', 'Danbury', 'Greenwich', 'Westport', 'Darien'],
        'New Haven County': ['New Haven', 'Milford', 'Waterbury', 'Meriden', 'Derby', 'Hamden'],
        'Hartford County': ['Hartford', 'West Hartford', 'Glastonbury', 'Enfield', 'Simsbury'],
        'New London County': ['Norwich', 'New London', 'Groton', 'Waterford'],
    },
    'Florida': {
        'Miami-Dade County': ['Miami', 'Coral Gables', 'Doral', 'Aventura', 'Homestead', 'Hialeah'],
        'Broward County': ['Fort Lauderdale', 'Hollywood', 'Pembroke Pines', 'Miramar', 'Coral Springs', 'Plantation'],
        'Palm Beach County': ['West Palm Beach', 'Boca Raton', 'Delray Beach', 'Boynton Beach', 'Jupiter'],
        'Orange County': ['Orlando', 'Kissimmee', 'Winter Park', 'Sanford'],
        'Hillsborough County': ['Tampa', 'Brandon', 'Plant City', 'Temple Terrace'],
        'Pinellas County': ['St. Petersburg', 'Clearwater', 'Largo', 'Dunedin'],
        'Duval County': ['Jacksonville', 'Jacksonville Beach', 'Orange Park'],
    },
    'California': {
        'Los Angeles County': ['Los Angeles', 'Long Beach', 'Glendale', 'Pasadena', 'Beverly Hills', 'Santa Monica', 'Burbank'],
        'San Diego County': ['San Diego', 'Chula Vista', 'El Cajon', 'Oceanside', 'Escondido', 'Carlsbad'],
        'Bay Area': ['San Francisco', 'San Jose', 'Oakland', 'Palo Alto', 'Mountain View', 'Cupertino', 'Sunnyvale'],
        'Orange County': ['Anaheim', 'Santa Ana', 'Irvine', 'Costa Mesa', 'Newport Beach', 'Huntington Beach'],
        'Riverside County': ['Riverside', 'Corona', 'Moreno Valley', 'Palm Springs', 'Temecula'],
        'Sacramento County': ['Sacramento', 'Elk Grove', 'Roseville', 'Rancho Cordova'],
    },
    'Texas': {
        'Harris County': ['Houston', 'Pasadena', 'Pearland', 'Sugar Land', 'The Woodlands', 'Katy'],
        'Dallas County': ['Dallas', 'Garland', 'Irving', 'Grand Prairie', 'Mesquite', 'Carrollton'],
        'Travis County': ['Austin', 'Round Rock', 'Cedar Park', 'Pflugerville', 'Georgetown'],
        'Bexar County': ['San Antonio', 'Leon Valley', 'Converse', 'Universal City'],
        'Tarrant County': ['Fort Worth', 'Arlington', 'Grapevine', 'Euless', 'Bedford'],
        'Collin County': ['Plano', 'McKinney', 'Frisco', 'Allen', 'Richardson'],
    },
    'Illinois': {
        'Cook County': ['Chicago', 'Evanston', 'Skokie', 'Oak Park', 'Naperville', 'Schaumburg'],
        'DuPage County': ['Wheaton', 'Downers Grove', 'Lombard', 'Glen Ellyn', 'Carol Stream'],
        'Lake County': ['Waukegan', 'North Chicago', 'Highland Park', 'Libertyville'],
    },
    'Ohio': {
        'Cuyahoga County': ['Cleveland', 'Cleveland Heights', 'Parma', 'Lakewood', 'Strongsville'],
        'Franklin County': ['Columbus', 'Dublin', 'Westerville', 'Gahanna', 'Grove City'],
        'Hamilton County': ['Cincinnati', 'Hyde Park', 'Blue Ash', 'Mason', 'West Chester'],
        'Montgomery County': ['Dayton', 'Kettering', 'Beavercreek', 'Centerville'],
    },
    'Georgia': {
        'Fulton County': ['Atlanta', 'Sandy Springs', 'Alpharetta', 'Roswell', 'Milton'],
        'DeKalb County': ['Decatur', 'Dunwoody', 'Tucker', 'Stone Mountain', 'Lithonia'],
        'Gwinnett County': ['Lawrenceville', 'Duluth', 'Suwanee', 'Norcross', 'Buford'],
        'Cobb County': ['Marietta', 'Smyrna', 'Kennesaw', 'Acworth', 'Powder Springs'],
    },
    'North Carolina': {
        'Mecklenburg County': ['Charlotte', 'Huntersville', 'Concord', 'Cornelius', 'Davidson'],
        'Wake County': ['Raleigh', 'Cary', 'Apex', 'Morrisville', 'Wake Forest'],
        'Guilford County': ['Greensboro', 'High Point'],
        'Forsyth County': ['Winston-Salem', 'Clemmons', 'Lewisville'],
    },
    'Virginia': {
        'Fairfax County': ['Fairfax', 'McLean', 'Vienna', 'Reston', 'Herndon', 'Burke'],
        'Arlington County': ['Arlington', 'Ballston', 'Clarendon', 'Pentagon City'],
        'Alexandria City': ['Old Town', 'Del Ray', 'Braddock Road'],
        'Richmond City': ['Richmond', 'Henrico', 'Chesterfield', 'Short Pump'],
    },
    'Maryland': {
        'Montgomery County': ['Rockville', 'Bethesda', 'Gaithersburg', 'Silver Spring', 'Chevy Chase'],
        'Prince George\'s County': ['College Park', 'Laurel', 'Bowie', 'Greenbelt', 'Upper Marlboro'],
        'Baltimore City': ['Downtown Baltimore', 'Harbor East', 'Fells Point', 'Canton'],
        'Baltimore County': ['Towson', 'Pikesville', 'Catonsville', 'Essex', 'Dundalk'],
    },
    'Massachusetts': {
        'Suffolk County': ['Boston', 'South Boston', 'Dorchester', 'East Boston', 'Roxbury'],
        'Middlesex County': ['Cambridge', 'Somerville', 'Lowell', 'Newton', 'Waltham', 'Medford'],
        'Norfolk County': ['Dedham', 'Quincy', 'Needham', 'Brookline', 'Wellesley'],
        'Essex County': ['Salem', 'Lynn', 'Peabody', 'Beverly', 'Lawrence'],
    },
    'Washington': {
        'King County': ['Seattle', 'Bellevue', 'Kirkland', 'Redmond', 'Renton', 'Kent'],
        'Pierce County': ['Tacoma', 'Lakewood', 'Federal Way', 'Puyallup'],
        'Snohomish County': ['Everett', 'Lynnwood', 'Marysville', 'Edmonds', 'Bothell'],
        'Spokane County': ['Spokane', 'Spokane Valley', 'Cheney'],
    },
    'Arizona': {
        'Maricopa County': ['Phoenix', 'Scottsdale', 'Tempe', 'Chandler', 'Mesa', 'Gilbert', 'Glendale'],
        'Pima County': ['Tucson', 'Marana', 'Sahuarita', 'Oro Valley'],
    },
    'Michigan': {
        'Wayne County': ['Detroit', 'Dearborn', 'Livonia', 'Westland', 'Taylor', 'Warren'],
        'Oakland County': ['Troy', 'Pontiac', 'Southfield', 'Royal Oak', 'Birmingham', 'Novi'],
        'Macomb County': ['Sterling Heights', 'Warren', 'Clinton Township', 'Mount Clemens'],
        'Kent County': ['Grand Rapids', 'Wyoming', 'Walker', 'Kentwood'],
    },
    'Minnesota': {
        'Hennepin County': ['Minneapolis', 'Bloomington', 'Plymouth', 'Eden Prairie', 'Minnetonka'],
        'Ramsey County': ['St. Paul', 'Maplewood', 'Roseville', 'Little Canada', 'Shoreview'],
        'Dakota County': ['Apple Valley', 'Burnsville', 'Eagan', 'Lakeville'],
    },
    'Colorado': {
        'Denver County': ['Denver', 'Lakewood', 'Aurora', 'Thornton', 'Arvada', 'Commerce City'],
        'Jefferson County': ['Lakewood', 'Wheat Ridge', 'Arvada', 'Golden'],
        'Arapahoe County': ['Aurora', 'Centennial', 'Englewood', 'Littleton', 'Greenwood Village'],
        'El Paso County': ['Colorado Springs', 'Fountain', 'Monument', 'Manitou Springs'],
    },
    'Washington DC': { 'District of Columbia': ['Downtown DC', 'Georgetown', 'Capitol Hill', 'Dupont Circle', 'Foggy Bottom', 'Shaw'] },
    // States without detailed breakdown — user selects state only
    'Alabama': { 'Major Cities': ['Birmingham', 'Montgomery', 'Huntsville', 'Mobile', 'Tuscaloosa'] },
    'Alaska': { 'Major Cities': ['Anchorage', 'Fairbanks', 'Juneau', 'Sitka', 'Ketchikan'] },
    'Arkansas': { 'Major Cities': ['Little Rock', 'Fort Smith', 'Fayetteville', 'Springdale', 'Jonesboro'] },
    'Delaware': { 'Major Cities': ['Wilmington', 'Dover', 'Newark', 'Middletown', 'Smyrna'] },
    'Hawaii': { 'Major Cities': ['Honolulu', 'Hilo', 'Kailua', 'Kaneohe', 'Pearl City'] },
    'Idaho': { 'Major Cities': ['Boise', 'Nampa', 'Meridian', 'Idaho Falls', 'Pocatello'] },
    'Indiana': { 'Major Cities': ['Indianapolis', 'Fort Wayne', 'Evansville', 'South Bend', 'Carmel'] },
    'Iowa': { 'Major Cities': ['Des Moines', 'Cedar Rapids', 'Davenport', 'Sioux City', 'Waterloo'] },
    'Kansas': { 'Major Cities': ['Wichita', 'Overland Park', 'Kansas City', 'Topeka', 'Olathe'] },
    'Kentucky': { 'Major Cities': ['Louisville', 'Lexington', 'Bowling Green', 'Owensboro', 'Covington'] },
    'Louisiana': { 'Major Cities': ['New Orleans', 'Baton Rouge', 'Shreveport', 'Metairie', 'Lafayette'] },
    'Maine': { 'Major Cities': ['Portland', 'Lewiston', 'Bangor', 'South Portland', 'Auburn'] },
    'Mississippi': { 'Major Cities': ['Jackson', 'Gulfport', 'Southaven', 'Hattiesburg', 'Biloxi'] },
    'Missouri': { 'Major Cities': ['Kansas City', 'St. Louis', 'Springfield', 'Columbia', 'Independence'] },
    'Montana': { 'Major Cities': ['Billings', 'Missoula', 'Great Falls', 'Bozeman', 'Butte'] },
    'Nebraska': { 'Major Cities': ['Omaha', 'Lincoln', 'Bellevue', 'Grand Island', 'Kearney'] },
    'Nevada': { 'Major Cities': ['Las Vegas', 'Henderson', 'Reno', 'North Las Vegas', 'Sparks'] },
    'New Hampshire': { 'Major Cities': ['Manchester', 'Nashua', 'Concord', 'Derry', 'Dover'] },
    'New Mexico': { 'Major Cities': ['Albuquerque', 'Las Cruces', 'Rio Rancho', 'Santa Fe', 'Roswell'] },
    'North Dakota': { 'Major Cities': ['Fargo', 'Bismarck', 'Grand Forks', 'Minot', 'West Fargo'] },
    'Oklahoma': { 'Major Cities': ['Oklahoma City', 'Tulsa', 'Norman', 'Broken Arrow', 'Lawton'] },
    'Oregon': { 'Major Cities': ['Portland', 'Eugene', 'Salem', 'Gresham', 'Hillsboro'] },
    'Rhode Island': { 'Major Cities': ['Providence', 'Cranston', 'Warwick', 'Pawtucket', 'East Providence'] },
    'South Carolina': { 'Major Cities': ['Columbia', 'Charleston', 'North Charleston', 'Mount Pleasant', 'Greenville'] },
    'South Dakota': { 'Major Cities': ['Sioux Falls', 'Rapid City', 'Aberdeen', 'Brookings', 'Watertown'] },
    'Tennessee': { 'Major Cities': ['Memphis', 'Nashville', 'Knoxville', 'Chattanooga', 'Clarksville'] },
    'Utah': { 'Major Cities': ['Salt Lake City', 'West Valley City', 'Provo', 'West Jordan', 'Orem'] },
    'Vermont': { 'Major Cities': ['Burlington', 'Essex', 'Rutland', 'South Burlington', 'Colchester'] },
    'West Virginia': { 'Major Cities': ['Charleston', 'Huntington', 'Parkersburg', 'Morgantown', 'Martinsburg'] },
    'Wisconsin': { 'Major Cities': ['Milwaukee', 'Madison', 'Green Bay', 'Kenosha', 'Racine'] },
    'Wyoming': { 'Major Cities': ['Cheyenne', 'Casper', 'Laramie', 'Gillette', 'Rock Springs'] },
};

// ─── Niche Taxonomy from nicheConfig.js ─────────────────────────────────────
const NICHE_TAXONOMY = {
    corporate: {
        label: 'Corporate',
        niches: [
            { key: 'law-firm', label: 'Law Firm', practiceAreas: ['Personal Injury', 'Family Law', 'Criminal Defense', 'Immigration', 'Elder Law', 'Business Law', 'Employment Law', 'Real Estate Law', 'Divorce', 'Bankruptcy', 'Estate Planning', 'Civil Litigation', 'Workers Comp'] },
            { key: 'lawyer', label: 'Lawyer (Solo)', practiceAreas: ['Personal Injury', 'Family Law', 'Criminal Defense', 'Immigration', 'Estate Planning', 'Divorce'] },
            { key: 'real-estate-agent', label: 'Real Estate Agent', practiceAreas: ['Residential', 'Commercial', 'Luxury', 'Investment Properties', 'New Construction'] },
            { key: 'realtor', label: 'Realtor', practiceAreas: ['Residential', 'Commercial', 'Luxury', 'Property Management'] },
            { key: 'cpa', label: 'CPA', practiceAreas: ['Tax Preparation', 'Audit & Assurance', 'Business Advisory', 'Forensic Accounting', 'Estate & Trust'] },
            { key: 'accountant', label: 'Accountant', practiceAreas: ['Bookkeeping', 'Tax Services', 'Payroll', 'Financial Reporting'] },
            { key: 'financial-advisor', label: 'Financial Advisor', practiceAreas: ['Wealth Management', 'Retirement Planning', 'Investment Management', 'Insurance Planning'] },
            { key: 'finance-team', label: 'Finance Team', practiceAreas: ['Banking', 'Insurance', 'Investment Banking', 'Credit Unions'] },
            { key: 'executive', label: 'Executive', practiceAreas: ['CEO / C-Suite', 'Board of Directors', 'VP-Level', 'Founder-Led'] },
            { key: 'sales-team', label: 'Sales Team', practiceAreas: ['B2B SaaS', 'Enterprise Sales', 'Account Management', 'SDR Teams'] },
            { key: 'consulting-firm', label: 'Consulting Firm', practiceAreas: ['Management Consulting', 'IT Consulting', 'Strategy', 'HR Consulting'] },
            { key: 'tech-startup', label: 'Tech Startup', practiceAreas: ['SaaS', 'Fintech', 'Healthtech', 'AI / ML', 'E-commerce'] },
        ]
    },
    medical: {
        label: 'Medical / Healthcare',
        niches: [
            { key: 'medical-clinics', label: 'Medical Clinics', practiceAreas: ['Primary Care', 'Urgent Care', 'Family Medicine', 'Internal Medicine', 'Multi-Specialty'] },
            { key: 'dental-practices', label: 'Dental Practices', practiceAreas: ['General Dentistry', 'Orthodontics', 'Cosmetic Dentistry', 'Oral Surgery', 'Pediatric Dentistry'] },
            { key: 'doctors', label: 'Physicians', practiceAreas: ['Cardiology', 'Dermatology', 'Neurology', 'Orthopedics', 'Pediatrics', 'OB-GYN'] },
            { key: 'nurses', label: 'Nursing Staff', practiceAreas: ['RN', 'NP', 'CRNA', 'Travel Nursing', 'Home Health'] },
            { key: 'med-spas', label: 'Med-Spas', practiceAreas: ['Aesthetic Injectors', 'Laser Treatments', 'Skin Care', 'Body Contouring', 'Anti-Aging'] },
            { key: 'veterinarians', label: 'Veterinary Clinics', practiceAreas: ['Small Animal', 'Large Animal', 'Emergency & Critical Care', 'Specialty Vet'] },
        ]
    },
    academic: {
        label: 'Academic / Education',
        niches: [
            { key: 'university-faculty', label: 'University Faculty', practiceAreas: ['STEM Departments', 'Humanities', 'Business Schools', 'Law Schools', 'Medical Schools'] },
            { key: 'professors', label: 'Professors', practiceAreas: ['Adjunct Faculty', 'Associate Professors', 'Full Professors', 'Department Chairs'] },
            { key: 'researchers', label: 'Research Scientists', practiceAreas: ['Biomedical Research', 'Physics', 'Computer Science', 'Social Sciences', 'Engineering'] },
        ]
    }
};

const STATUSES = ['Scraped', 'Disqualified', 'Audited', 'Emailed', 'Opened', 'Clicked Link', 'Value Bump Sent', 'Follow-Up Clicked', 'Converted'];
const STATUS_STYLES = {
    Disqualified: 'bg-red-900/60 text-red-300 border border-red-700',
    Audited: 'bg-yellow-900/60 text-yellow-200 border border-yellow-700',
    Emailed: 'bg-blue-900/60 text-blue-300 border border-blue-700',
    Opened: 'bg-indigo-900/60 text-indigo-300 border border-indigo-700',
    'Clicked Link': 'bg-purple-900/60 text-purple-300 border border-purple-700',
    'Value Bump Sent': 'bg-cyan-900/60 text-cyan-300 border border-cyan-700',
    'Follow-Up Clicked': 'bg-pink-900/60 text-pink-300 border border-pink-700',
    Converted: 'bg-green-900/60 text-green-300 border border-green-700',
    Scraped: 'bg-gray-700/60 text-gray-300 border border-gray-600',
};

// ─── Sub-components ───────────────────────────────────────────────────────────
const StatCard = ({ label, value, color = 'text-[#FACC15]', sub }) => (
    <div className="bg-[#1a1a1a] border border-gray-700/60 p-5 rounded-xl flex flex-col gap-1 hover:border-gray-600 transition-colors">
        <p className="text-gray-400 text-xs uppercase tracking-widest font-semibold">{label}</p>
        <p className={`text-4xl font-bold ${color}`}>{value ?? '—'}</p>
        {sub && <p className="text-gray-500 text-xs mt-0.5">{sub}</p>}
    </div>
);

const SelectField = ({ label, value, onChange, children, disabled }) => (
    <div className="flex flex-col gap-1 min-w-[140px]">
        <label className="text-xs text-gray-400 font-semibold uppercase tracking-wider">{label}</label>
        <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
            className={`bg-[#0e0e0e] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-[#FACC15] focus:outline-none appearance-none cursor-pointer ${disabled ? 'opacity-40 cursor-not-allowed' : 'hover:border-gray-500'}`}>
            {children}
        </select>
    </div>
);

const Toast = ({ message, type, onClose }) => (
    <div className={`fixed bottom-6 right-6 z-[100] flex items-center gap-3 px-5 py-4 rounded-xl shadow-2xl border text-sm font-semibold
        ${type === 'success' ? 'bg-green-900 border-green-600 text-green-200' : 'bg-red-900 border-red-600 text-red-200'}`}>
        <span>{type === 'success' ? '✅' : '❌'}</span>
        <span>{message}</span>
        <button onClick={onClose} className="ml-2 opacity-70 hover:opacity-100 text-xl">×</button>
    </div>
);

// ─── Live Scraper Log Panel ───────────────────────────────────────────────────
const ScraperLogPanel = ({ visible }) => {
    const [logs, setLogs] = useState([]);
    const [running, setRunning] = useState(false);
    const [elapsed, setElapsed] = useState(null);
    const bottomRef = useRef(null);

    useEffect(() => {
        if (!visible) return;
        const poll = async () => {
            try {
                const [logsRes, statusRes] = await Promise.all([
                    fetch(`${API}/api/scraper/logs`),
                    fetch(`${API}/api/scraper/status`)
                ]);
                const logsData = await logsRes.json();
                const statusData = await statusRes.json();
                setLogs(logsData.logs || []);
                setRunning(statusData.running || false);
                setElapsed(statusData.elapsedSeconds);
            } catch { }
        };
        poll();
        const id = setInterval(poll, 2000);
        return () => clearInterval(id);
    }, [visible]);

    useEffect(() => {
        if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }, [logs]);

    if (!visible) return null;

    return (
        <div className="bg-[#0a0a0a] border border-gray-700/60 rounded-xl overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-700/40">
                <div className={`w-2 h-2 rounded-full ${running ? 'bg-green-400 animate-pulse' : logs.length ? 'bg-gray-500' : 'bg-gray-700'}`}></div>
                <span className="text-xs font-bold text-gray-300 uppercase tracking-wider">
                    {running ? `Scraper Running... (${elapsed}s elapsed)` : logs.length ? 'Last Scout Log' : 'No scraper log yet — run a scout to see live output'}
                </span>
                {running && <span className="ml-auto text-xs text-green-400 animate-pulse">LIVE</span>}
            </div>
            <div className="h-64 overflow-y-auto p-4 font-mono text-xs text-gray-400 leading-relaxed">
                {logs.length === 0 ? (
                    <p className="text-gray-700 italic">Waiting for scout output...</p>
                ) : logs.map((line, i) => (
                    <div key={i} className={`py-0.5 ${line.includes('ERROR') || line.includes('SKIP') ? 'text-red-400' :
                        line.includes('SAVED') || line.includes('DONE') ? 'text-green-400' :
                            line.includes('=====') ? 'text-[#FACC15]' :
                                line.includes('SCOUTING:') || line.includes('Query:') ? 'text-white' :
                                    'text-gray-400'
                        }`}>{line}</div>
                ))}
                <div ref={bottomRef} />
            </div>
        </div>
    );
};

// ─── Main Dashboard ────────────────────────────────────────────────────────────
const PLAYBOOK_HOOKS = {
    'FRANKENSTEIN_EFFECT': {
        name: 'Frankenstein Effect (Visual Inconsistency)',
        copy: `"Hi [Name], noticed you've hired recently but the team page still has grey placeholders or mismatched photos with inconsistent lighting/backgrounds. That can leak trust when clients look for a powerhouse firm. Most firms use us to run a 10-minute Virtual Photo Day instead of coordinating a photographer. Can I send a complimentary 3-person pilot to show how your unified team page would look?"`,
        trigger: 'FRANKENSTEIN'
    },
    'NEW_HIRE_TRIGGER': {
        name: 'New Hire Trigger',
        copy: `"Hi [Name], congratulations on the recent team additions! I noticed how complex it must be to maintain consistent headshots as new hires join. Instead of scheduling photographers, new hires receive an automated invite and generate a consistent, firm-approved headshot in minutes. Can I set up a complimentary credit for your next hire to test this out?"`,
        trigger: 'NEW_HIRE'
    },
    'WEBSITE_REFRESH': {
        name: 'Website Refresh & Brand Update',
        copy: `"Hi [Name], looking at your website and team page—are you planning a design refresh or update soon? Usually, updating attorney headshots is a scheduling and logistics nightmare. We've built a stateless workflow that lets your entire roster refresh their headshots in 10 minutes without photographers visiting the office. Can I show you a 2-minute demo?"`,
        trigger: 'WEBSITE_REFRESH'
    },
    'CREDIBILITY_LEAKAGE': {
        name: 'Credibility Leakage (High Stakes client-trust)',
        copy: `"Hi [Name], in high-stakes professional services, mismatched partner profiles subconsciously leak trust with new prospects. We turn headshot standardisation into a seamless day-one onboarding step. We set aside complimentary credits for your firm to test the visual quality. You can activate it here: [Link]"`,
        trigger: 'CREDIBILITY_LEAKAGE'
    },
    'GENERIC': {
        name: 'Generic Outreach',
        copy: `"Hi [Name], we help professional firms streamline corporate identity and headshots. New hires generate a consistent, brand-approved headshot in minutes without scheduling photo days. I'd love to share a complimentary 3-person evaluation credit with your team. Let me know if you are open to checking it out."`,
        trigger: 'UNKNOWN'
    }
};

const GrowthDashboard = () => {
    const [leads, setLeads] = useState([]);
    const [stats, setStats] = useState({});
    const [loading, setLoading] = useState(true);
    const [utmData, setUtmData] = useState({});
    const [selectedDraft, setSelectedDraft] = useState(null);
    const [emailInput, setEmailInput] = useState('');
    const [targetRoleInput, setTargetRoleInput] = useState('');
    const [toast, setToast] = useState(null);
    const [showLog, setShowLog] = useState(false);
    const [sending, setSending] = useState(false);
    const [selectedLeads, setSelectedLeads] = useState(new Set());

    // GTM Playbook & Validation states
    const [activeTab, setActiveTab] = useState('pipeline');
    const [segmentMetrics, setSegmentMetrics] = useState({ verticals: [], triggers: [], templates: [], buyers: [] });
    const [playbookTemplate, setPlaybookTemplate] = useState('FRANKENSTEIN_EFFECT');
    const [draftBuyerPersona, setDraftBuyerPersona] = useState('UNKNOWN');
    const [draftTemplateType, setDraftTemplateType] = useState('GENERIC');
    const [draftPriorityScore, setDraftPriorityScore] = useState(0);
    const [draftFrictionScore, setDraftFrictionScore] = useState(0);
    const [draftReplyType, setDraftReplyType] = useState('NO_RESPONSE');
    const [draftDisqualificationReason, setDraftDisqualificationReason] = useState('NONE');
    const [draftCurrentSolution, setDraftCurrentSolution] = useState('UNKNOWN');
    const [draftPilotInvited, setDraftPilotInvited] = useState('');
    const [draftPilotRequested, setDraftPilotRequested] = useState('');
    const [draftPilotStarted, setDraftPilotStarted] = useState('');
    const [draftPilotCompleted, setDraftPilotCompleted] = useState('');
    const [draftConverted, setDraftConverted] = useState('');
    const [pmfMetrics, setPmfMetrics] = useState(null);
    const [pmfLoading, setPmfLoading] = useState(true);

    const [activeVertical, setActiveVertical] = useState('Law');
    const [targetSizeMin, setTargetSizeMin] = useState(10);
    const [targetSizeMax, setTargetSizeMax] = useState(50);
    const [activeBuyerPersona, setActiveBuyerPersona] = useState('OFFICE_MANAGER');

    // Scout state
    const [enforceIcp, setEnforceIcp] = useState(true);
    const [scoutCategory, setScoutCategory] = useState('');
    const [scoutNiche, setScoutNiche] = useState('');
    const [scoutPracticeArea, setScoutPracticeArea] = useState('');
    const [scoutState, setScoutState] = useState('New Jersey');
    const [scoutCounty, setScoutCounty] = useState('');
    const [scoutCity, setScoutCity] = useState('');
    const [directUrl, setDirectUrl] = useState('');
    const [scouting, setScouting] = useState(false);

    // Filters
    const [filterStatus, setFilterStatus] = useState('');
    const [filterNiche, setFilterNiche] = useState('');
    const [filterRegion, setFilterRegion] = useState('');

    const getIcpTarget = useCallback(() => {
        const v = (activeVertical || 'Law').toLowerCase();
        if (v.includes('law') || v.includes('legal')) {
            return { category: 'corporate', niche: 'law-firm', practiceArea: '', label: 'Law Firm' };
        }
        if (v.includes('medical') || v.includes('doctor') || v.includes('clinic')) {
            return { category: 'medical', niche: 'medical-clinics', practiceArea: '', label: 'Medical Clinics' };
        }
        if (v.includes('cpa') || v.includes('account')) {
            return { category: 'corporate', niche: 'cpa', practiceArea: '', label: 'CPA' };
        }
        if (v.includes('real estate')) {
            return { category: 'corporate', niche: 'real-estate-agent', practiceArea: '', label: 'Real Estate Agent' };
        }
        return { category: 'corporate', niche: 'law-firm', practiceArea: '', label: 'Law Firm' };
    }, [activeVertical]);

    const icpTarget = getIcpTarget();
    const displayCategory = enforceIcp ? icpTarget.category : scoutCategory;
    const displayNiche = enforceIcp ? icpTarget.niche : scoutNiche;
    const displayPracticeArea = enforceIcp ? icpTarget.practiceArea : scoutPracticeArea;

    // Derived geo options
    const availableCounties = GEO_HIERARCHY[scoutState] ? Object.keys(GEO_HIERARCHY[scoutState]) : [];
    const availableCities = (scoutState && scoutCounty && GEO_HIERARCHY[scoutState]?.[scoutCounty]) || [];

    // Derived niche options
    const selectedCategoryData = displayCategory ? NICHE_TAXONOMY[displayCategory] : null;
    const availableNiches = selectedCategoryData?.niches ?? [];
    const selectedNicheData = availableNiches.find(n => n.key === displayNiche);
    const availablePracticeAreas = selectedNicheData?.practiceAreas ?? [];

    const showToast = useCallback((msg, type = 'success') => {
        setToast({ message: msg, type });
        setTimeout(() => setToast(null), 5000);
    }, []);

    const fetchStats = useCallback(async () => {
        try { setStats(await (await fetch(`${API}/api/stats`)).json()); } catch { }
    }, []);

    const fetchLeads = useCallback(async (showLoad = true) => {
        if (showLoad) setLoading(true);
        const p = new URLSearchParams();
        if (filterStatus) p.set('status', filterStatus);
        if (filterNiche) p.set('niche', filterNiche);
        if (filterRegion) p.set('region', filterRegion);
        try {
            const data = await (await fetch(`${API}/api/leads?${p}`)).json();
            setLeads(data.leads || []);
        } catch { }
        finally { if (showLoad) setLoading(false); }
    }, [filterStatus, filterNiche, filterRegion]);

    const fetchSegmentMetrics = useCallback(async () => {
        try {
            const data = await (await fetch(`${API}/api/segment-metrics`)).json();
            setSegmentMetrics(data);
        } catch { }
    }, []);

    const fetchPmfMetrics = useCallback(async () => {
        try {
            const res = await fetch(`${API}/api/pmf/metrics`);
            if (res.ok) {
                const data = await res.json();
                setPmfMetrics(data);
            }
        } catch (e) {
            console.error("Error fetching PMF metrics:", e);
        } finally {
            setPmfLoading(false);
        }
    }, []);

    const fetchActiveIcp = useCallback(async () => {
        try {
            const res = await fetch(`${API}/api/active-icp`);
            if (res.ok) {
                const data = await res.json();
                setActiveVertical(data.active_vertical || 'Law');
                setTargetSizeMin(data.target_size_min ?? 10);
                setTargetSizeMax(data.target_size_max ?? 50);
                setPlaybookTemplate(data.active_template_type || 'FRANKENSTEIN_EFFECT');
                setActiveBuyerPersona(data.active_buyer_persona || 'OFFICE_MANAGER');
            }
        } catch { }
    }, []);

    const handleSaveActiveIcp = async () => {
        const derivedTrigger = PLAYBOOK_HOOKS[playbookTemplate]?.trigger || 'UNKNOWN';
        try {
            const res = await fetch(`${API}/api/active-icp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    active_vertical: activeVertical,
                    target_size_min: targetSizeMin,
                    target_size_max: targetSizeMax,
                    active_template_type: playbookTemplate,
                    active_buyer_persona: activeBuyerPersona,
                    active_trigger_event: derivedTrigger
                })
            });
            if (res.ok) {
                showToast('Active ICP campaign settings successfully applied!', 'success');
            } else {
                showToast('Failed to save active ICP settings.', 'error');
            }
        } catch {
            showToast('Error connecting to backend to save settings.', 'error');
        }
    };

    useEffect(() => {
        fetchLeads(); fetchStats(); fetchSegmentMetrics(); fetchActiveIcp(); fetchPmfMetrics();
        setUtmData({ source: localStorage.getItem('gh_referral_source'), date: localStorage.getItem('gh_referral_date') });
        const id = setInterval(() => { fetchLeads(false); fetchStats(); fetchSegmentMetrics(); fetchPmfMetrics(); }, 8000);
        return () => clearInterval(id);
    }, [fetchLeads, fetchStats, fetchSegmentMetrics, fetchActiveIcp, fetchPmfMetrics]);

    useEffect(() => {
        if (activeVertical === 'Law') {
            setScoutCategory('corporate');
            setScoutNiche('law-firm');
            setScoutPracticeArea('Personal Injury');
        } else if (activeVertical === 'CPA') {
            setScoutCategory('corporate');
            setScoutNiche('cpa');
            setScoutPracticeArea('Tax Preparation');
        } else if (activeVertical === 'Consulting') {
            setScoutCategory('corporate');
            setScoutNiche('consulting-firm');
            setScoutPracticeArea('Management Consulting');
        }
    }, [activeVertical]);

    useEffect(() => { fetchLeads(); }, [filterStatus, filterNiche, filterRegion, fetchLeads]);

    const handleOpenDraft = (lead) => {
        setSelectedDraft(lead);
        setEmailInput(lead.Email || '');
        setTargetRoleInput(lead.Target_Role || '');
        setDraftBuyerPersona(lead.Buyer_Persona || 'UNKNOWN');
        setDraftTemplateType(lead.Outreach_Template_Type || 'GENERIC');
        setDraftPriorityScore(lead.Priority_Score || 0);
        setDraftFrictionScore(lead.Friction_Score || 0);
        setDraftReplyType(lead.reply_type || 'NO_RESPONSE');
        setDraftDisqualificationReason(lead.disqualification_reason || 'NONE');
        setDraftCurrentSolution(lead.current_solution || 'UNKNOWN');
        setDraftPilotInvited(lead.Date_Pilot_Invited || '');
        setDraftPilotRequested(lead.Date_Pilot_Requested || '');
        setDraftPilotStarted(lead.Date_Pilot_Started || '');
        setDraftPilotCompleted(lead.Date_Pilot_Completed || '');
        setDraftConverted(lead.Date_Converted || '');
    };

    const handleUpdateUrl = async (id, status, url) => {
        try {
            await fetch(`${API}/api/leads/${id}`, {
                method: 'PUT', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Detected_Team_URL: url, Status: status })
            });
            fetchLeads(false);
        } catch { }
    };

    const handleDeleteLead = async (id, firmName) => {
        if (!window.confirm(`Remove "${firmName}" from the pipeline?`)) return;
        try {
            const res = await fetch(`${API}/api/leads/${id}`, { method: 'DELETE' });
            if (!res.ok) throw new Error();
            setLeads(prev => prev.filter(l => l.id !== id));
            fetchStats();
            showToast(`"${firmName}" removed.`);
        } catch { showToast('Delete failed — is GTM server running on port 3003?', 'error'); }
    };

    const handleBulkDelete = async () => {
        if (selectedLeads.size === 0) return;
        if (!window.confirm(`Permanently remove ${selectedLeads.size} leads from the pipeline?`)) return;
        try {
            await Promise.all(Array.from(selectedLeads).map(id => fetch(`${API}/api/leads/${id}`, { method: 'DELETE' })));
            showToast(`Eliminated ${selectedLeads.size} leads.`);
            setSelectedLeads(new Set());
            fetchLeads(false); fetchStats();
        } catch { showToast('Could not reach GTM server (port 3003).', 'error'); }
    };

    const handleRunAudit = async (id) => {
        try {
            await fetch(`${API}/api/leads/${id}/audit`, { method: 'POST' });
            showToast('Audit triggered — Playwright is running in background (~30s).');
        } catch { showToast('Audit failed to trigger.', 'error'); }
    };

    const handleDirectScout = async () => {
        if (!directUrl) return;
        setScouting(true);
        setShowLog(true);
        const nicheLabel = selectedNicheData?.label || undefined;
        try {
            const res = await fetch(`${API}/api/scraper/direct`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url: directUrl, label: scoutPracticeArea || nicheLabel || scoutCategory || undefined })
            });
            const data = await res.json();
            showToast(data.running ? `Direct Inject started for ${directUrl} — watch the Live Log` : data.message);
            setDirectUrl('');

            let polls = 0;
            const aggressivePoll = setInterval(() => {
                fetchLeads(false); fetchStats();
                if (++polls >= 6) clearInterval(aggressivePoll); // 6 * 5s = 30s
            }, 5000);
        } catch { showToast('Could not reach GTM server (port 3003).', 'error'); }
        finally { setScouting(false); }
    };

    const handleRunScout = async () => {
        setScouting(true);
        setShowLog(true);
        const nicheLabel = selectedNicheData?.label || undefined;
        try {
            const res = await fetch(`${API}/api/scraper/run`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    niche: displayPracticeArea || (selectedNicheData?.key) || undefined,
                    region: scoutCity || scoutCounty || undefined,
                    state: scoutState || undefined,
                    label: displayPracticeArea || nicheLabel || undefined,
                })
            });
            const data = await res.json();
            showToast(data.running ? `Scout started: "${data.query}" — watch the Live Log below` : data.message);
            // Poll leads more aggressively for 90s after starting
            let polls = 0;
            const aggressivePoll = setInterval(() => {
                fetchLeads(false); fetchStats();
                if (++polls >= 18) clearInterval(aggressivePoll); // 18 * 5s = 90s
            }, 5000);
        } catch { showToast('Could not reach GTM server (port 3003). Is it running?', 'error'); }
        finally { setScouting(false); }
    };

    const handleManualAudit = async (leadId, file) => {
        if (!file) return;
        showToast('Uploading screenshot & starting manual audit...', 'success');
        const formData = new FormData();
        formData.append('screenshot', file);

        try {
            const res = await fetch(`${API}/api/leads/${leadId}/manual-audit`, {
                method: 'POST',
                body: formData
            });
            if (res.ok) {
                showToast('Manual audit started! Watch the logs.', 'success');
                fetchStats(); fetchLeads(false);
            } else {
                const data = await res.json();
                showToast(`Error: ${data.error}`, 'error');
            }
        } catch { showToast('Upload failed — is GTM server on port 3003 running?', 'error'); }
        // Reset file input by clearing its value (handled implicitly by React here or user re-click)
    };

    const handleSendEmail = async () => {
        if (!emailInput) { showToast('Enter an email address first.', 'error'); return; }
        if (selectedDraft.Status === 'Emailed') {
            if (!window.confirm(`WARNING: This lead is already marked 'Emailed'.\n\nAre you sure you want to SEND A DUPLICATE to ${emailInput}?`)) return;
        }
        setSending(true);
        try {
            const res = await fetch(`${API}/api/leads/${selectedDraft.id}/send-email`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ toEmail: emailInput, overrideDraft: selectedDraft.Outreach_Draft })
            });
            const data = await res.json();
            if (!res.ok) { showToast(`Error: ${data.error}`, 'error'); return; }
            showToast(`Email sent to ${emailInput} via Zoho SMTP!`);
            setSelectedDraft(null); fetchLeads(false); fetchStats();
        } catch { showToast('Failed to send email — is GTM server on port 3003 running?', 'error'); }
        finally { setSending(false); }
    };

    const handleSendFollowUp = async () => {
        if (!emailInput) { showToast('Enter an email address first.', 'error'); return; }
        if (selectedDraft.Status === 'Converted') {
            if (!window.confirm(`WARNING: This lead is already marked 'Converted'.\n\nAre you sure you want to SEND A FOLLOW-UP to ${emailInput}?`)) return;
        }
        setSending(true);
        try {
            const res = await fetch(`${API}/api/leads/${selectedDraft.id}/send-followup`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ toEmail: emailInput })
            });
            const data = await res.json();
            if (!res.ok) { showToast(`Error: ${data.error}`, 'error'); return; }
            showToast(`Value Bump Follow-Up sent to ${emailInput}!`);
            setSelectedDraft(null); fetchLeads(false); fetchStats();
        } catch { showToast('Failed to send follow-up — is GTM server on port 3003 running?', 'error'); }
        finally { setSending(false); }
    };

    const handleSaveDraft = async () => {
        if (!selectedDraft) return;
        setSending(true);
        const derivedTrigger = PLAYBOOK_HOOKS[draftTemplateType]?.trigger || 'UNKNOWN';
        try {
            const res = await fetch(`${API}/api/leads/${selectedDraft.id}/draft`, {
                method: 'PUT', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: emailInput,
                    draft: selectedDraft.Outreach_Draft,
                    targetRole: targetRoleInput,
                    triggerEvent: derivedTrigger,
                    buyerPersona: draftBuyerPersona,
                    templateType: draftTemplateType,
                    priorityScore: draftPriorityScore,
                    frictionScore: draftFrictionScore,
                    replyType: draftReplyType,
                    disqualificationReason: draftDisqualificationReason,
                    currentSolution: draftCurrentSolution,
                    pilotInvited: draftPilotInvited,
                    pilotRequested: draftPilotRequested,
                    pilotStarted: draftPilotStarted,
                    pilotCompleted: draftPilotCompleted,
                    dateConverted: draftConverted
                })
            });
            if (!res.ok) throw new Error('Failed to save');
            showToast('Draft and Contact Info permanently saved!');
            fetchLeads(false);
            setSelectedDraft(null); // Close modal on successful save to reflect changes
        } catch {
            showToast('Error saving draft', 'error');
        } finally {
            setSending(false);
        }
    };

    const performSearch = (firmName) =>
        window.open(`https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(firmName + ' ("Managing Partner" OR "Owner" OR "COO" OR "Administrator" OR "Office Manager")')}`, '_blank');

    return (
        <div className="w-full min-h-screen bg-[#0e0e0e] text-white p-6 lg:p-8" style={{ fontFamily: '"Plus Jakarta Sans","Noto Sans",sans-serif' }}>

            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-[#FACC15] tracking-tight">GTM Command Center</h1>
                    <p className="text-gray-500 text-sm mt-1">Lead Pipeline — Hypothesis Validation Engine</p>
                </div>
                <div className="flex items-center gap-4">
                    {/* Navigation Tabs */}
                    <div className="flex bg-[#1a1a1a] rounded-lg p-1 border border-gray-700/80 shadow-lg">
                        <button onClick={() => setActiveTab('pipeline')}
                            className={`px-4 py-2 text-xs font-bold rounded-md transition-all ${activeTab === 'pipeline' ? 'bg-[#FACC15] text-black shadow' : 'text-gray-400 hover:text-white'}`}>
                            Command Center
                        </button>
                        <button onClick={() => setActiveTab('playbook')}
                            className={`px-4 py-2 text-xs font-bold rounded-md transition-all ${activeTab === 'playbook' ? 'bg-[#FACC15] text-black shadow' : 'text-gray-400 hover:text-white'}`}>
                            ICP Strategy Hub
                        </button>
                        <button onClick={() => setActiveTab('pmf')}
                            className={`px-4 py-2 text-xs font-bold rounded-md transition-all ${activeTab === 'pmf' ? 'bg-[#FACC15] text-black shadow' : 'text-gray-400 hover:text-white'}`}>
                            PMF Discovery
                        </button>
                    </div>
                    <div className="hidden sm:flex items-center gap-2 text-xs text-gray-500 bg-gray-900 border border-gray-700 px-3 py-2 rounded-lg">
                        <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse inline-block"></span>
                        Auto-refreshing every 8s
                    </div>
                </div>
            </div>

            {activeTab === 'pipeline' && (
                <>
                    {/* Stat Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3 mb-6">
                        <StatCard label="Total Leads" value={stats.total} color="text-[#FACC15]" />
                        <StatCard label="Scraped" value={stats.scraped} color="text-gray-300" />
                        <StatCard label="Disqualified" value={stats.disqualified} color="text-red-400" />
                        <StatCard label="Audited" value={stats.audited} color="text-yellow-300" />
                        <StatCard label="Emailed" value={stats.emailed} color="text-blue-400" />
                        <StatCard label="Clicked Link" value={stats.clickedLink} color="text-purple-400" />
                        <StatCard label="Converted" value={stats.converted} color="text-green-400"
                            sub={stats.converted ? `$${(stats.converted * 1700).toLocaleString()} ARR` : 'No conversions yet'} />
                    </div>

                    {/* Scout + UTM */}
                    <div className="grid grid-cols-1 xl:grid-cols-4 gap-4 mb-4">
                        <div className="xl:col-span-3 bg-[#1a1a1a] border border-gray-700/60 rounded-xl p-5">
                            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                                <div className="flex items-center gap-2">
                                    <span className="text-lg">🔍</span>
                                    <h2 className="text-base font-bold text-white">Smart Scout</h2>
                                    <span className="text-xs text-gray-500 ml-1">— Target specific niches and geographies</span>
                                </div>
                                <label className="inline-flex items-center cursor-pointer bg-gray-800/40 border border-gray-700/60 rounded-lg px-3 py-1.5 transition hover:bg-gray-800/80">
                                    <input 
                                        type="checkbox" 
                                        checked={enforceIcp} 
                                        onChange={e => setEnforceIcp(e.target.checked)} 
                                        className="sr-only peer" 
                                    />
                                    <div className="relative w-7 h-4 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all dark:border-gray-600 peer-checked:bg-yellow-400"></div>
                                    <span className="ms-2 text-xs font-bold text-gray-300 peer-checked:text-yellow-400">Enforce Saved ICP ({activeVertical || 'Law'})</span>
                                </label>
                            </div>

                            {/* WHO */}
                            <p className="text-[10px] text-gray-600 uppercase tracking-widest font-bold mb-2">Who to Target</p>
                            <div className="flex flex-wrap gap-3 items-end mb-4">
                                <SelectField label="Category" value={displayCategory} onChange={v => { setScoutCategory(v); setScoutNiche(''); setScoutPracticeArea(''); }} disabled={enforceIcp}>
                                    <option value="">All Categories</option>
                                    {Object.entries(NICHE_TAXONOMY).map(([k, c]) => <option key={k} value={k}>{c.label}</option>)}
                                </SelectField>
                                <SelectField label="Niche" value={displayNiche} onChange={v => { setScoutNiche(v); setScoutPracticeArea(''); }} disabled={enforceIcp || !displayCategory}>
                                    <option value="">All Niches</option>
                                    {availableNiches.map(n => <option key={n.key} value={n.key}>{n.label}</option>)}
                                </SelectField>
                                <SelectField label="Practice Area" value={displayPracticeArea} onChange={setScoutPracticeArea} disabled={enforceIcp || !displayNiche}>
                                    <option value="">Any Practice Area</option>
                                    {availablePracticeAreas.map(pa => <option key={pa} value={pa}>{pa}</option>)}
                                </SelectField>
                            </div>

                            <div className="border-t border-gray-700/40 my-4"></div>

                            {/* WHERE */}
                            <p className="text-[10px] text-gray-600 uppercase tracking-widest font-bold mb-2">Where to Target</p>
                            <div className="flex flex-wrap gap-3 items-end mb-5">
                                <SelectField label="State" value={scoutState} onChange={v => { setScoutState(v); setScoutCounty(''); setScoutCity(''); }}>
                                    <option value="">Any State</option>
                                    {Object.keys(GEO_HIERARCHY).sort().map(s => <option key={s} value={s}>{s}</option>)}
                                </SelectField>
                                <SelectField label="County / Region" value={scoutCounty} onChange={v => { setScoutCounty(v); setScoutCity(''); }} disabled={!scoutState || availableCounties.length === 0}>
                                    <option value="">Any County</option>
                                    {availableCounties.map(c => <option key={c} value={c}>{c}</option>)}
                                </SelectField>
                                <SelectField label="City" value={scoutCity} onChange={setScoutCity} disabled={!scoutCounty || availableCities.length === 0}>
                                    <option value="">Any City</option>
                                    {availableCities.map(c => <option key={c} value={c}>{c}</option>)}
                                </SelectField>
                            </div>

                            {/* Summary + buttons */}
                            <div className="flex flex-wrap items-center gap-4 pt-3 border-t border-gray-700/40">
                                <div className="flex-1 min-w-0 text-xs text-gray-400">
                                    {(displayCategory || scoutState) ? (
                                        <>Targeting: <span className="text-white font-semibold">
                                            {[displayPracticeArea, selectedNicheData?.label, selectedCategoryData?.label].filter(Boolean).join(' › ') || 'All'}
                                        </span> in <span className="text-white font-semibold">
                                                {[scoutCity, scoutCounty, scoutState].filter(Boolean).join(', ')}
                                            </span></>
                                    ) : <span className="italic text-gray-700">Select category and location to build a targeted query</span>}
                                </div>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="text"
                                        placeholder="Direct Add URL (e.g. thebarnesfirm.com)"
                                        value={directUrl}
                                        onChange={e => setDirectUrl(e.target.value)}
                                        className="bg-[#0e0e0e] border border-gray-700 rounded-lg px-3 py-2.5 text-xs text-white focus:border-[#FACC15] focus:outline-none w-64 md:w-48"
                                    />
                                    <button onClick={handleDirectScout} disabled={scouting || !directUrl}
                                        className={`text-xs px-3 py-2.5 rounded-lg transition-all font-bold ${(!directUrl || scouting) ? 'bg-gray-800 text-gray-500 cursor-not-allowed' : 'bg-green-600 hover:bg-green-500 text-white'}`}>
                                        Inject URL
                                    </button>
                                    <button onClick={() => setShowLog(v => !v)}
                                        className="text-xs text-gray-400 hover:text-white border border-gray-700 px-3 py-2 rounded-lg transition ml-2">
                                        {showLog ? 'Hide Log' : 'Live Log'}
                                    </button>
                                    <button onClick={handleRunScout} disabled={scouting}
                                        className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-bold text-sm transition-all shadow-lg
                                            ${scouting ? 'bg-gray-700 text-gray-400 cursor-not-allowed' : 'bg-[#FACC15] hover:bg-yellow-300 text-black shadow-yellow-900/30'}`}>
                                        {scouting ? <><span className="animate-spin inline-block">⌛</span> Starting...</> : '▶ Run Scout'}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* UTM */}
                        <div className="bg-[#1a1a1a] border border-gray-700/60 p-5 rounded-xl">
                            <h2 className="text-sm font-bold text-white mb-4 uppercase tracking-wider">UTM Session</h2>
                            <div className="space-y-3 text-sm">
                                <div><p className="text-gray-500 text-xs mb-0.5">Source</p>
                                    <p className="text-white font-mono font-semibold">{utmData.source || 'Direct'}</p></div>
                                <div><p className="text-gray-500 text-xs mb-0.5">Captured</p>
                                    <p className="text-white font-mono text-xs">{utmData.date ? new Date(utmData.date).toLocaleString() : 'N/A'}</p></div>
                                <div className="pt-2 border-t border-gray-700/40">
                                    <p className="text-gray-500 text-xs mb-0.5">Pipeline Value</p>
                                    <p className="text-green-400 font-bold text-lg">${((stats.converted || 0) * 1700).toLocaleString()}</p></div>
                            </div>
                        </div>
                    </div>

                    {/* Live Scraper Log */}
                    <div className="mb-6">
                        <ScraperLogPanel visible={showLog} />
                    </div>

                    {/* Lead Table */}
                    <div className="bg-[#1a1a1a] border border-gray-700/60 rounded-xl overflow-hidden">
                        <div className="p-5 border-b border-gray-700/60 flex flex-wrap items-center gap-3">
                            <h2 className="text-base font-bold text-white mr-1">Lead Review</h2>
                            <div className="flex bg-[#0e0e0e] rounded-lg p-1 border border-gray-700/80 mr-2 overflow-x-auto">
                                <button
                                    onClick={() => setFilterStatus('')}
                                    className={`whitespace-nowrap px-3 py-1.5 text-xs font-bold rounded-md transition-all ${filterStatus === '' ? 'bg-gray-700 text-white shadow' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}>
                                    All Leads
                                </button>
                                {STATUSES.map(statusTab => (
                                    <button
                                        key={statusTab}
                                        onClick={() => setFilterStatus(statusTab)}
                                        className={`whitespace-nowrap px-3 py-1.5 text-xs font-bold rounded-md transition-all ${filterStatus === statusTab ? 'bg-[#FACC15] text-black shadow' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}>
                                        {statusTab}
                                    </button>
                                ))}
                            </div>
                            <select value={filterNiche} onChange={e => setFilterNiche(e.target.value)}
                                className="bg-[#0e0e0e] border border-gray-700 rounded-lg px-3 py-1.5 text-xs text-gray-300 focus:border-[#FACC15] focus:outline-none appearance-none cursor-pointer">
                                <option value="">All Niches</option>
                                {Object.entries(NICHE_TAXONOMY).map(([catKey, cat]) => (
                                    <optgroup key={catKey} label={cat.label}>
                                        {cat.niches.map(n => <option key={n.key} value={n.label}>{n.label}</option>)}
                                    </optgroup>
                                ))}
                            </select>
                            <select value={filterRegion} onChange={e => setFilterRegion(e.target.value)}
                                className="bg-[#0e0e0e] border border-gray-700 rounded-lg px-3 py-1.5 text-xs text-gray-300 focus:border-[#FACC15] focus:outline-none appearance-none cursor-pointer">
                                <option value="">All Regions</option>
                                {Object.entries(GEO_HIERARCHY).sort().map(([state, counties]) => (
                                    <optgroup key={state} label={state}>
                                        {Object.keys(counties).map(co => <option key={co} value={co}>{co}</option>)}
                                    </optgroup>
                                ))}
                            </select>
                            {(filterStatus || filterNiche || filterRegion) && (
                                <button onClick={() => { setFilterStatus(''); setFilterNiche(''); setFilterRegion(''); }}
                                    className="text-xs text-gray-500 hover:text-white transition underline">Clear filters</button>
                            )}
                            {selectedLeads.size > 0 && (
                                <button onClick={handleBulkDelete}
                                    className="text-xs text-red-500 hover:text-red-400 font-bold uppercase transition bg-red-500/10 px-2 py-1 rounded">Delete {selectedLeads.size} Selected</button>
                            )}
                            <span className="ml-auto text-xs text-gray-500">{leads.length} leads shown</span>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse text-sm">
                                <thead>
                                    <tr className="bg-[#141414] border-b border-gray-700/60 text-xs text-gray-400 uppercase tracking-wider">
                                        <th className="p-4 w-10">
                                            <input type="checkbox" className="rounded bg-gray-900 border-gray-700 cursor-pointer"
                                                checked={leads.length > 0 && selectedLeads.size === leads.length}
                                                onChange={e => {
                                                    if (e.target.checked) setSelectedLeads(new Set(leads.map(l => l.id)));
                                                    else setSelectedLeads(new Set());
                                                }} />
                                        </th>
                                        <th className="p-4">Firm Name</th>
                                        <th className="p-4">Team URL</th>
                                        <th className="p-4 text-center">Size</th>
                                        <th className="p-4 text-center" title="Locations">Locs</th>
                                        <th className="p-4">Niche / Location</th>
                                        <th className="p-4">Target Role</th>
                                        <th className="p-4">Status</th>
                                        <th className="p-4">Contact Info</th>
                                        <th className="p-4 text-right">ARR</th>
                                        <th className="p-4 text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan="11" className="p-8 text-center text-gray-500 animate-pulse">Loading leads…</td></tr>
                                    ) : leads.length === 0 ? (
                                        <tr><td colSpan="11" className="p-8 text-center text-gray-600">No leads found. Use Smart Scout.</td></tr>
                                    ) : leads.map(lead => (
                                        <tr key={lead.id} className="border-b border-gray-800/40 hover:bg-gray-800/20 transition-colors">
                                            <td className="p-4">
                                                <input type="checkbox" className="rounded bg-gray-900 border-gray-700 cursor-pointer"
                                                    checked={selectedLeads.has(lead.id)}
                                                    onChange={e => {
                                                        const next = new Set(selectedLeads);
                                                        if (e.target.checked) next.add(lead.id);
                                                        else next.delete(lead.id);
                                                        setSelectedLeads(next);
                                                    }} />
                                            </td>
                                            <td className="p-4 font-semibold text-white max-w-[180px]">
                                                <div className="flex flex-col gap-1 items-start">
                                                    <span className="block truncate w-full" title={lead.Firm_Name}>{lead.Firm_Name}</span>
                                                    <div className="flex gap-1 flex-wrap">
                                                        {(lead.Priority_Score > 0 || lead.Friction_Score > 0) && (
                                                            <span className="bg-gray-800 text-gray-300 text-[9px] px-1 rounded font-bold">
                                                                P: <span className="text-green-400">{lead.Priority_Score || 0}</span> | F: <span className="text-red-400">{lead.Friction_Score || 0}</span>
                                                            </span>
                                                        )}
                                                        {lead.Team_Size >= 10 && lead.Team_Size <= 50 && (
                                                            <span className="bg-amber-500/20 text-amber-300 text-[9px] uppercase font-bold px-1.5 py-0.5 rounded border border-amber-500/30">ICP Sweet Spot</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="p-4 max-w-[220px]">
                                                <div className="flex items-center gap-1.5 w-full">
                                                    <input type="text" defaultValue={lead.Detected_Team_URL}
                                                        onBlur={e => { if (e.target.value !== lead.Detected_Team_URL) handleUpdateUrl(lead.id, lead.Status, e.target.value); }}
                                                        className="bg-gray-900/60 border border-gray-700 p-1.5 w-full rounded text-[10px] font-mono focus:border-[#FACC15] focus:outline-none text-gray-300" />
                                                    <a href={lead.Detected_Team_URL} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white transition group focus:outline-none" title="Open in new tab">
                                                        <svg className="w-4 h-4 outline-none" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                                    </a>
                                                </div>
                                            </td>
                                            <td className="p-4 text-center">
                                                <span className={`font-bold text-base ${!lead.Team_Size ? 'text-gray-600' : lead.Team_Size < 5 ? 'text-red-400' : lead.Team_Size >= 15 ? 'text-green-400' : 'text-yellow-300'}`}>
                                                    {lead.Team_Size || '?'}
                                                </span>
                                            </td>
                                            <td className="p-4 text-center">
                                                <span className={`font-bold text-sm ${lead.Location_Count > 1 ? 'text-purple-400' : 'text-gray-500'}`}>
                                                    {lead.Location_Count || 1}
                                                </span>
                                            </td>
                                            <td className="p-4 text-xs">
                                                <span className="block text-gray-200">{lead.Niche || '—'}</span>
                                                <span className="text-gray-500">{lead.Region || ''}</span>
                                                {lead.Trigger_Event && lead.Trigger_Event !== 'UNKNOWN' && (
                                                    <span className="mt-1 inline-block bg-yellow-950 text-yellow-300 text-[9px] px-1 rounded border border-yellow-800 uppercase font-mono font-bold">{lead.Trigger_Event.replace('_', ' ')}</span>
                                                )}
                                            </td>
                                            <td className="p-4 text-xs font-medium text-blue-300">
                                                {lead.Target_Role || 'Undefined'}
                                            </td>
                                            <td className="p-4">
                                                <select value={lead.Status || 'Scraped'} onChange={e => handleUpdateUrl(lead.id, e.target.value, lead.Detected_Team_URL)}
                                                    className={`px-2.5 py-1 rounded-full text-xs font-bold focus:outline-none appearance-none cursor-pointer ${STATUS_STYLES[lead.Status] || STATUS_STYLES.Scraped}`}>
                                                    {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                                                </select>
                                            </td>
                                            <td className="p-4 text-xs">
                                                {['Emailed', 'Opened', 'Clicked Link', 'Value Bump Sent', 'Follow-Up Clicked', 'Converted'].includes(lead.Status) ? (
                                                    <div className="flex flex-col gap-0.5">
                                                        <span className="font-bold text-white mb-0.5">{lead.Contact_Name || 'Unknown Name'}</span>
                                                        <span className="text-blue-300 font-mono mb-1">{lead.Email || 'No Email'}</span>
                                                        <div className="bg-gray-900 border border-gray-700/50 rounded flex flex-col p-1.5 gap-0.5 mt-0.5 shadow-sm">
                                                            {lead.Date_Emailed && <span className="text-blue-400 text-[10px] font-mono flex gap-1 items-center"><span>📨</span> Delivered: {new Date(lead.Date_Emailed).toLocaleString()}</span>}
                                                            {lead.Date_Opened && <span className="text-indigo-400 text-[10px] font-mono flex gap-1 items-center"><span>👀</span> Opened: {new Date(lead.Date_Opened).toLocaleString()}</span>}
                                                            {lead.Date_Clicked && <span className="text-purple-400 text-[10px] font-mono flex gap-1 items-center"><span>🖱️</span> Clicked: {new Date(lead.Date_Clicked).toLocaleString()}</span>}
                                                            {lead.Date_Converted && <span className="text-green-400 text-[10px] font-mono flex gap-1 items-center drop-shadow-[0_0_2px_rgba(74,222,128,0.4)]"><span>⭐</span> Converted: {new Date(lead.Date_Converted).toLocaleString()}</span>}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-700 font-medium">— Waiting to Send —</span>
                                                )}
                                            </td>
                                            <td className="p-4 font-mono text-sm text-right">
                                                <span className={lead.Status === 'Converted' ? 'text-green-400 font-bold' : 'text-gray-700'}>
                                                    {lead.Status === 'Converted' ? '$1,700' : '$0'}
                                                </span>
                                            </td>
                                            <td className="p-4">
                                                <div className="flex items-center gap-1.5 justify-center flex-wrap">
                                                    <button onClick={() => performSearch(lead.Firm_Name)}
                                                        className="bg-blue-800/70 hover:bg-blue-700 text-blue-200 px-2.5 py-1 rounded text-xs transition border border-blue-700/50">Find Contact</button>
                                                    <button onClick={() => handleRunAudit(lead.id)}
                                                        className="bg-gray-700/70 hover:bg-gray-600 text-gray-200 px-2.5 py-1 rounded text-xs transition border border-gray-600/50">
                                                        {lead.Status === 'Audited' ? 'Re-Audit' : 'Run Audit'}</button>
                                                    <label className="bg-purple-800/70 hover:bg-purple-700 text-purple-200 px-2.5 py-1 rounded text-xs transition border border-purple-700/50 cursor-pointer flex items-center justify-center">
                                                        Upload IMG
                                                        <input type="file" className="hidden" accept="image/*" onChange={(e) => { handleManualAudit(lead.id, e.target.files[0]); e.target.value = null; }} />
                                                    </label>
                                                    {lead.Outreach_Draft && (
                                                        <button onClick={() => handleOpenDraft(lead)}
                                                            className={`px-2.5 py-1 rounded text-xs font-bold transition ${lead.Status === 'Clicked Link' ? 'bg-green-500 hover:bg-green-400 text-black shadow-lg shadow-green-500/20' : 'bg-[#FACC15]/90 hover:bg-yellow-400 text-black'}`}>
                                                            {lead.Status === 'Clicked Link' ? 'Value Bump Pending' : 'View Draft'}
                                                        </button>
                                                    )}
                                                    <button onClick={() => handleDeleteLead(lead.id, lead.Firm_Name)}
                                                        className="bg-red-900/50 hover:bg-red-700/80 text-red-400 hover:text-red-200 w-7 h-7 rounded flex items-center justify-center text-sm transition border border-red-800/40 font-bold">✕</button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}

            {/* ICP Playbook Tab */}
            {activeTab === 'playbook' && (
                <div className="space-y-6">
                    {/* Dynamic Customizer Section */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="bg-[#1a1a1a] border border-gray-700/60 rounded-xl p-5 space-y-4">
                            <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                <span>🎯</span> GTM Playbook Variables
                            </h3>
                            <p className="text-xs text-gray-400 leading-relaxed">
                                Playbook playground. Dynamically view copywriting hooks and validation parameters decoupled from hardwired assumptions.
                            </p>
                            <div className="space-y-3">
                                <div>
                                    <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1">Vertical Beachhead</label>
                                    <select value={activeVertical} onChange={e => setActiveVertical(e.target.value)}
                                        className="w-full bg-[#0e0e0e] border border-gray-700 rounded-lg p-2.5 text-white text-sm focus:border-[#FACC15] focus:outline-none">
                                        <option value="Law">Law Firms & Attorney Partnerships</option>
                                        <option value="CPA">CPA, Tax Advisory & Accounting</option>
                                        <option value="Consulting">Boutique Management & IT Consulting</option>
                                        <option value="Other">Other Services / General</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1">Target Organization Size</label>
                                    <select value={`${targetSizeMin}-${targetSizeMax}`} onChange={e => {
                                        const [min, max] = e.target.value.split('-').map(Number);
                                        setTargetSizeMin(min);
                                        setTargetSizeMax(max);
                                    }}
                                        className="w-full bg-[#0e0e0e] border border-gray-700 rounded-lg p-2.5 text-white text-sm focus:border-[#FACC15] focus:outline-none">
                                        <option value="10-50">10 – 50 Employees (Sweet Spot)</option>
                                        <option value="50-200">50 – 200 Employees (Mid-Market)</option>
                                        <option value="200-1000">200 – 1000 Employees (Large)</option>
                                        <option value="1-1000">1 – 1000 Employees (All Sizes)</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1">Active Copywriting Hook</label>
                                    <select value={playbookTemplate} onChange={e => setPlaybookTemplate(e.target.value)}
                                        className="w-full bg-[#0e0e0e] border border-gray-700 rounded-lg p-2.5 text-white text-sm focus:border-[#FACC15] focus:outline-none">
                                        {Object.entries(PLAYBOOK_HOOKS).map(([k, t]) => (
                                            <option key={k} value={k}>{t.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1">Active Buyer Persona</label>
                                    <select value={activeBuyerPersona} onChange={e => setActiveBuyerPersona(e.target.value)}
                                        className="w-full bg-[#0e0e0e] border border-gray-700 rounded-lg p-2.5 text-white text-sm focus:border-[#FACC15] focus:outline-none">
                                        <option value="MANAGING_PARTNER">Managing Partner / Owner</option>
                                        <option value="OFFICE_MANAGER">Office Manager / Administrator</option>
                                        <option value="OPERATIONS_DIRECTOR">Operations Director / VP</option>
                                        <option value="HR_LEAD">HR Lead / Head of Talent</option>
                                    </select>
                                </div>
                                <button onClick={handleSaveActiveIcp}
                                    className="w-full bg-[#FACC15] hover:bg-yellow-400 text-black text-xs font-bold py-2.5 px-4 rounded-lg transition mt-2">
                                    💾 Save & Apply Active Campaign
                                </button>
                            </div>
                        </div>

                        {/* Dynamic Copy Preview */}
                        <div className="lg:col-span-2 bg-[#1a1a1a] border border-gray-700/60 rounded-xl p-5 flex flex-col justify-between">
                            <div>
                                <div className="flex justify-between items-center mb-3">
                                    <h3 className="text-lg font-bold text-white">Dynamic Copy Template</h3>
                                    <span className="text-[10px] bg-amber-500/20 text-[#FACC15] px-2 py-0.5 rounded font-bold uppercase tracking-wider">Hypothesis Hook</span>
                                </div>
                                <div className="bg-[#0e0e0e] border border-gray-800 rounded-xl p-4 min-h-[120px] text-gray-300 text-sm font-mono leading-relaxed select-all">
                                    {PLAYBOOK_HOOKS[playbookTemplate]?.copy}
                                </div>
                            </div>
                            <div className="flex justify-end mt-4">
                                <button onClick={() => { navigator.clipboard.writeText(PLAYBOOK_HOOKS[playbookTemplate]?.copy); showToast('Playbook hook copied!'); }}
                                    className="bg-[#FACC15] hover:bg-yellow-400 text-black text-xs font-bold py-2 px-4 rounded-lg transition">
                                    Copy Hook Text
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Hypothesis Performance Matrices */}
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                        
                        {/* Verticals Table */}
                        <div className="bg-[#1a1a1a] border border-gray-700/60 rounded-xl p-5">
                            <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4">1. Vertical & Headcount Segments</h3>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-xs border-collapse">
                                    <thead>
                                        <tr className="bg-[#0e0e0e] border-b border-gray-800 text-gray-400 uppercase tracking-wider">
                                            <th className="p-3 font-bold">Segment</th>
                                            <th className="p-3 font-bold">Size</th>
                                            <th className="p-3 text-center font-bold">Sent</th>
                                            <th className="p-3 text-center font-bold">Clicked</th>
                                            <th className="p-3 text-center font-bold font-mono">CTR</th>
                                            <th className="p-3 text-center font-bold">Pilot Conv.</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-800/40 text-gray-300">
                                        {(!segmentMetrics.verticals || segmentMetrics.verticals.length === 0) ? (
                                            <tr><td colSpan="6" className="p-4 text-center text-gray-600 italic">No outreach data yet</td></tr>
                                        ) : segmentMetrics.verticals.map((row, idx) => {
                                            const ctr = row.Sent ? ((row.Clicked / row.Sent) * 100).toFixed(1) : '0.0';
                                            const convRate = row.Sent ? ((row.Converted / row.Sent) * 100).toFixed(1) : '0.0';
                                            return (
                                                <tr key={idx} className="hover:bg-gray-800/10">
                                                    <td className="p-3 font-semibold text-white">{row.SegmentName}</td>
                                                    <td className="p-3">{row.SizeRange}</td>
                                                    <td className="p-3 text-center font-mono">{row.Sent}</td>
                                                    <td className="p-3 text-center font-mono">{row.Clicked}</td>
                                                    <td className="p-3 text-center font-mono font-bold text-purple-400">{ctr}%</td>
                                                    <td className="p-3 text-center font-mono font-bold text-green-400">{convRate}%</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Triggers Table */}
                        <div className="bg-[#1a1a1a] border border-gray-700/60 rounded-xl p-5">
                            <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4">2. Trigger Event Performance</h3>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-xs border-collapse">
                                    <thead>
                                        <tr className="bg-[#0e0e0e] border-b border-gray-800 text-gray-400 uppercase tracking-wider">
                                            <th className="p-3 font-bold">Trigger Event</th>
                                            <th className="p-3 text-center font-bold">Sent</th>
                                            <th className="p-3 text-center font-bold">Clicked</th>
                                            <th className="p-3 text-center font-bold font-mono">CTR</th>
                                            <th className="p-3 text-center font-bold">Pilot Conv.</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-800/40 text-gray-300">
                                        {(!segmentMetrics.triggers || segmentMetrics.triggers.length === 0) ? (
                                            <tr><td colSpan="5" className="p-4 text-center text-gray-600 italic">No outreach data yet</td></tr>
                                        ) : segmentMetrics.triggers.map((row, idx) => {
                                            const ctr = row.Sent ? ((row.Clicked / row.Sent) * 100).toFixed(1) : '0.0';
                                            const convRate = row.Sent ? ((row.Converted / row.Sent) * 100).toFixed(1) : '0.0';
                                            return (
                                                <tr key={idx} className="hover:bg-gray-800/10">
                                                    <td className="p-3 font-semibold text-white font-mono">{row.SegmentName}</td>
                                                    <td className="p-3 text-center font-mono">{row.Sent}</td>
                                                    <td className="p-3 text-center font-mono">{row.Clicked}</td>
                                                    <td className="p-3 text-center font-mono font-bold text-purple-400">{ctr}%</td>
                                                    <td className="p-3 text-center font-mono font-bold text-green-400">{convRate}%</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Template Types Table */}
                        <div className="bg-[#1a1a1a] border border-gray-700/60 rounded-xl p-5">
                            <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4">3. Messaging Hook Performance</h3>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-xs border-collapse">
                                    <thead>
                                        <tr className="bg-[#0e0e0e] border-b border-gray-800 text-gray-400 uppercase tracking-wider">
                                            <th className="p-3 font-bold">Template Type</th>
                                            <th className="p-3 text-center font-bold">Sent</th>
                                            <th className="p-3 text-center font-bold">Clicked</th>
                                            <th className="p-3 text-center font-bold font-mono">CTR</th>
                                            <th className="p-3 text-center font-bold">Pilot Conv.</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-800/40 text-gray-300">
                                        {(!segmentMetrics.templates || segmentMetrics.templates.length === 0) ? (
                                            <tr><td colSpan="5" className="p-4 text-center text-gray-600 italic">No outreach data yet</td></tr>
                                        ) : segmentMetrics.templates.map((row, idx) => {
                                            const ctr = row.Sent ? ((row.Clicked / row.Sent) * 100).toFixed(1) : '0.0';
                                            const convRate = row.Sent ? ((row.Converted / row.Sent) * 100).toFixed(1) : '0.0';
                                            return (
                                                <tr key={idx} className="hover:bg-gray-800/10">
                                                    <td className="p-3 font-semibold text-white">{row.SegmentName}</td>
                                                    <td className="p-3 text-center font-mono">{row.Sent}</td>
                                                    <td className="p-3 text-center font-mono">{row.Clicked}</td>
                                                    <td className="p-3 text-center font-mono font-bold text-purple-400">{ctr}%</td>
                                                    <td className="p-3 text-center font-mono font-bold text-green-400">{convRate}%</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Buyer Personas Table */}
                        <div className="bg-[#1a1a1a] border border-gray-700/60 rounded-xl p-5">
                            <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4">4. Buyer Persona Performance</h3>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-xs border-collapse">
                                    <thead>
                                        <tr className="bg-[#0e0e0e] border-b border-gray-800 text-gray-400 uppercase tracking-wider">
                                            <th className="p-3 font-bold">Buyer Persona</th>
                                            <th className="p-3 text-center font-bold">Sent</th>
                                            <th className="p-3 text-center font-bold">Clicked</th>
                                            <th className="p-3 text-center font-bold font-mono">CTR</th>
                                            <th className="p-3 text-center font-bold">Pilot Conv.</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-800/40 text-gray-300">
                                        {(!segmentMetrics.buyers || segmentMetrics.buyers.length === 0) ? (
                                            <tr><td colSpan="5" className="p-4 text-center text-gray-600 italic">No outreach data yet</td></tr>
                                        ) : segmentMetrics.buyers.map((row, idx) => {
                                            const ctr = row.Sent ? ((row.Clicked / row.Sent) * 100).toFixed(1) : '0.0';
                                            const convRate = row.Sent ? ((row.Converted / row.Sent) * 100).toFixed(1) : '0.0';
                                            return (
                                                <tr key={idx} className="hover:bg-gray-800/10">
                                                    <td className="p-3 font-semibold text-white font-mono">{row.SegmentName}</td>
                                                    <td className="p-3 text-center font-mono">{row.Sent}</td>
                                                    <td className="p-3 text-center font-mono">{row.Clicked}</td>
                                                    <td className="p-3 text-center font-mono font-bold text-purple-400">{ctr}%</td>
                                                    <td className="p-3 text-center font-mono font-bold text-green-400">{convRate}%</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                    </div>
                </div>
            )}

            {activeTab === 'pmf' && (
                <div className="space-y-6">
                    {/* Funnel Component */}
                    <div className="bg-[#1a1a1a] border border-gray-700/60 rounded-xl p-6 shadow-xl">
                        <div className="flex justify-between items-center mb-6">
                            <div>
                                <h2 className="text-lg font-bold text-white">Pilot Activation Funnel</h2>
                                <p className="text-gray-500 text-xs mt-0.5">Track conversion velocity across pilot activation milestones</p>
                            </div>
                            <span className="text-[10px] text-gray-400 bg-gray-800 px-3 py-1 rounded-full uppercase tracking-wider font-bold">
                                Funnel Volume: {pmfMetrics?.funnel?.total_outreach || 0} Leads
                            </span>
                        </div>

                        {pmfLoading ? (
                            <div className="py-12 text-center text-gray-500 animate-pulse text-sm">Loading PMF metrics...</div>
                        ) : !pmfMetrics ? (
                            <div className="py-12 text-center text-gray-500 text-sm italic">No PMF metrics available</div>
                        ) : (
                            <div className="grid grid-cols-1 lg:grid-cols-6 gap-4 relative">
                                {/* Step 1: Sent */}
                                <div className="bg-[#0f0f0f] border border-gray-800 rounded-xl p-4 flex flex-col justify-between">
                                    <div>
                                        <div className="text-[10px] text-gray-500 uppercase tracking-wider font-bold">1. Outreach Sent</div>
                                        <div className="text-2xl font-mono font-bold text-white mt-2">{pmfMetrics.funnel.sent || 0}</div>
                                    </div>
                                    <div className="mt-4 text-[10px] text-gray-600">Base level outreach volume</div>
                                </div>

                                {/* Step 2: Invited */}
                                <div className="bg-[#0f0f0f] border border-gray-800 rounded-xl p-4 flex flex-col justify-between relative">
                                    <div>
                                        <div className="text-[10px] text-gray-500 uppercase tracking-wider font-bold">2. Pilot Invited</div>
                                        <div className="text-2xl font-mono font-bold text-blue-400 mt-2">{pmfMetrics.funnel.invited || 0}</div>
                                    </div>
                                    <div className="mt-4 text-[10px] text-blue-500/80">
                                        Conversion: {pmfMetrics.funnel.sent ? ((pmfMetrics.funnel.invited / pmfMetrics.funnel.sent) * 100).toFixed(1) : '0.0'}%
                                    </div>
                                </div>

                                {/* Step 3: Requested */}
                                <div className="bg-[#0f0f0f] border border-gray-800 rounded-xl p-4 flex flex-col justify-between">
                                    <div>
                                        <div className="text-[10px] text-gray-500 uppercase tracking-wider font-bold">3. Requested</div>
                                        <div className="text-2xl font-mono font-bold text-[#FACC15] mt-2">{pmfMetrics.funnel.requested || 0}</div>
                                    </div>
                                    <div className="mt-4 text-[10px] text-yellow-500/80">
                                        Conversion: {pmfMetrics.funnel.invited ? ((pmfMetrics.funnel.requested / pmfMetrics.funnel.invited) * 100).toFixed(1) : '0.0'}%
                                        {pmfMetrics.funnel.avg_sent_to_req !== null && pmfMetrics.funnel.avg_sent_to_req !== undefined && (
                                            <div className="text-[9px] text-gray-500 mt-1">Avg. velocity: {parseFloat(pmfMetrics.funnel.avg_sent_to_req).toFixed(1)} days</div>
                                        )}
                                    </div>
                                </div>

                                {/* Step 4: Started */}
                                <div className="bg-[#0f0f0f] border border-gray-800 rounded-xl p-4 flex flex-col justify-between">
                                    <div>
                                        <div className="text-[10px] text-gray-500 uppercase tracking-wider font-bold">4. Pilot Started</div>
                                        <div className="text-2xl font-mono font-bold text-orange-400 mt-2">{pmfMetrics.funnel.started || 0}</div>
                                    </div>
                                    <div className="mt-4 text-[10px] text-orange-500/80">
                                        Conversion: {pmfMetrics.funnel.requested ? ((pmfMetrics.funnel.started / pmfMetrics.funnel.requested) * 100).toFixed(1) : '0.0'}%
                                        {pmfMetrics.funnel.avg_req_to_start !== null && pmfMetrics.funnel.avg_req_to_start !== undefined && (
                                            <div className="text-[9px] text-gray-500 mt-1">Avg. velocity: {parseFloat(pmfMetrics.funnel.avg_req_to_start).toFixed(1)} days</div>
                                        )}
                                    </div>
                                </div>

                                {/* Step 5: Completed */}
                                <div className="bg-[#0f0f0f] border border-gray-800 rounded-xl p-4 flex flex-col justify-between">
                                    <div>
                                        <div className="text-[10px] text-gray-500 uppercase tracking-wider font-bold">5. Pilot Done</div>
                                        <div className="text-2xl font-mono font-bold text-purple-400 mt-2">{pmfMetrics.funnel.completed || 0}</div>
                                    </div>
                                    <div className="mt-4 text-[10px] text-purple-500/80">
                                        Conversion: {pmfMetrics.funnel.started ? ((pmfMetrics.funnel.completed / pmfMetrics.funnel.started) * 100).toFixed(1) : '0.0'}%
                                        {pmfMetrics.funnel.avg_start_to_comp !== null && pmfMetrics.funnel.avg_start_to_comp !== undefined && (
                                            <div className="text-[9px] text-gray-500 mt-1">Avg. velocity: {parseFloat(pmfMetrics.funnel.avg_start_to_comp).toFixed(1)} days</div>
                                        )}
                                    </div>
                                </div>

                                {/* Step 6: Converted */}
                                <div className="bg-[#0f0f0f] border border-green-900/60 rounded-xl p-4 flex flex-col justify-between">
                                    <div>
                                        <div className="text-[10px] text-green-400 uppercase tracking-wider font-bold">6. Paid Conv.</div>
                                        <div className="text-2xl font-mono font-bold text-green-400 mt-2">{pmfMetrics.funnel.converted || 0}</div>
                                    </div>
                                    <div className="mt-4 text-[10px] text-green-500/80">
                                        Conversion: {pmfMetrics.funnel.completed ? ((pmfMetrics.funnel.converted / pmfMetrics.funnel.completed) * 100).toFixed(1) : '0.0'}%
                                        {pmfMetrics.funnel.avg_comp_to_paid !== null && pmfMetrics.funnel.avg_comp_to_paid !== undefined && (
                                            <div className="text-[9px] text-gray-500 mt-1">Avg. velocity: {parseFloat(pmfMetrics.funnel.avg_comp_to_paid).toFixed(1)} days</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Insights Block */}
                    <div className="bg-[#1a1a1a] border border-gray-700/60 rounded-xl p-6 shadow-xl">
                        <h2 className="text-base font-bold text-white mb-4">Statistical Significance Insights</h2>
                        {pmfLoading ? (
                            <div className="py-6 text-center text-gray-500 animate-pulse text-xs">Computing insights...</div>
                        ) : !pmfMetrics?.insights || pmfMetrics.insights.length === 0 ? (
                            <div className="text-xs text-gray-500 italic p-4 bg-[#0e0e0e] border border-gray-800 rounded-lg">
                                Waiting for sufficient data sample sizes to run statistical significance calculations (requires 5+ conversions or high segment response volume).
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {pmfMetrics.insights.map((insight, idx) => (
                                    <div key={idx} className={`p-4 rounded-xl border flex flex-col justify-between ${
                                        insight.type === 'SUCCESS' ? 'bg-green-950/20 border-green-800/40 text-green-300' :
                                        insight.type === 'CAUTION' ? 'bg-red-950/20 border-red-800/40 text-red-300' :
                                        'bg-gray-950/20 border-gray-800 text-gray-300'
                                    }`}>
                                        <div>
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                                                    insight.type === 'SUCCESS' ? 'bg-green-900/40 text-green-400' :
                                                    insight.type === 'CAUTION' ? 'bg-red-900/40 text-red-400' :
                                                    'bg-gray-800 text-gray-400'
                                                }`}>
                                                    {insight.type}
                                                </span>
                                                <span className="text-[10px] font-mono text-gray-500">{insight.dimension}</span>
                                            </div>
                                            <p className="text-sm font-semibold leading-snug">{insight.statement}</p>
                                        </div>
                                        {insight.confidence !== undefined && (
                                            <div className="mt-3 text-[10px] text-gray-500 flex justify-between font-mono">
                                                <span>Confidence: {(insight.confidence * 100).toFixed(0)}%</span>
                                                <span>p-value: {insight.p_value ? insight.p_value.toFixed(4) : 'N/A'}</span>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Hypothesis Leaderboard & Breakdown Grid */}
                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                        <div className="xl:col-span-2 bg-[#1a1a1a] border border-gray-700/60 rounded-xl p-5 shadow-xl">
                            <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4">Hypothesis Leaderboard</h3>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-xs border-collapse">
                                    <thead>
                                        <tr className="bg-[#0e0e0e] border-b border-gray-800 text-gray-400 uppercase tracking-wider font-bold">
                                            <th className="p-3 font-mono">Hypothesis ID</th>
                                            <th className="p-3">Target/Hook</th>
                                            <th className="p-3 text-center">Sent</th>
                                            <th className="p-3 text-center">Replies</th>
                                            <th className="p-3 text-center">Invited</th>
                                            <th className="p-3 text-center">Pilots</th>
                                            <th className="p-3 text-center">Paid</th>
                                            <th className="p-3 text-center text-[#FACC15]">Activation</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-800/40 text-gray-300">
                                        {pmfLoading ? (
                                            <tr><td colSpan="8" className="p-4 text-center text-gray-500 animate-pulse">Loading...</td></tr>
                                        ) : !pmfMetrics?.hypotheses || pmfMetrics.hypotheses.length === 0 ? (
                                            <tr><td colSpan="8" className="p-4 text-center text-gray-600 italic">No hypotheses tracked yet</td></tr>
                                        ) : pmfMetrics.hypotheses.map((h, idx) => {
                                            const activationRate = h.emails ? ((h.pilots / h.emails) * 100).toFixed(1) : '0.0';
                                            return (
                                                <tr key={idx} className="hover:bg-gray-800/10 transition-colors">
                                                    <td className="p-3 font-bold text-white font-mono text-xs">{h.hypothesis_key}</td>
                                                    <td className="p-3">
                                                        <div className="flex flex-wrap gap-1 max-w-[280px]">
                                                            <span className="bg-blue-900/30 text-blue-400 text-[8px] font-bold px-1.5 py-0.5 rounded">{h.vertical}</span>
                                                            <span className="bg-purple-900/30 text-purple-400 text-[8px] font-bold px-1.5 py-0.5 rounded">{h.buyer_persona}</span>
                                                            <span className="bg-yellow-900/30 text-yellow-400 text-[8px] font-bold px-1.5 py-0.5 rounded">{h.trigger_event}</span>
                                                            <span className="bg-pink-900/30 text-pink-400 text-[8px] font-bold px-1.5 py-0.5 rounded">{h.pain_indicator}</span>
                                                        </div>
                                                    </td>
                                                    <td className="p-3 text-center font-mono">{h.emails}</td>
                                                    <td className="p-3 text-center font-mono">{h.replies}</td>
                                                    <td className="p-3 text-center font-mono">{h.invited || 0}</td>
                                                    <td className="p-3 text-center font-mono text-yellow-500 font-bold">{h.pilots}</td>
                                                    <td className="p-3 text-center font-mono text-green-500 font-bold">{h.conversions}</td>
                                                    <td className="p-3 text-center font-mono text-[#FACC15] font-bold">{activationRate}%</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Breakdown Leaderboards */}
                        <div className="space-y-6">
                            {/* Triggers Breakdown */}
                            <div className="bg-[#1a1a1a] border border-gray-700/60 rounded-xl p-5 shadow-xl">
                                <h3 className="text-xs font-bold text-white uppercase tracking-wider mb-3">Top Triggers (Why Now?)</h3>
                                <div className="space-y-2">
                                    {pmfLoading ? (
                                        <div className="text-center text-xs text-gray-500 animate-pulse py-4">Loading...</div>
                                    ) : !pmfMetrics?.triggers || pmfMetrics.triggers.length === 0 ? (
                                        <div className="text-xs text-gray-600 italic py-2">No trigger data</div>
                                    ) : pmfMetrics.triggers.map((t, idx) => (
                                        <div key={idx} className="bg-[#0f0f0f] border border-gray-800 rounded-lg p-3 flex justify-between items-center text-xs">
                                            <div>
                                                <div className="font-bold text-white font-mono">{t.name}</div>
                                                <div className="text-gray-500 text-[10px] mt-0.5">{t.total} sent, {t.replies} replies</div>
                                            </div>
                                            <div className="text-right">
                                                <div className="font-bold text-[#FACC15]">{t.total ? ((t.pilots / t.total) * 100).toFixed(1) : '0.0'}%</div>
                                                <div className="text-[9px] text-gray-500">Pilot Rate</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Pains Breakdown */}
                            <div className="bg-[#1a1a1a] border border-gray-700/60 rounded-xl p-5 shadow-xl">
                                <h3 className="text-xs font-bold text-white uppercase tracking-wider mb-3">Top Pain Indicators</h3>
                                <div className="space-y-2">
                                    {pmfLoading ? (
                                        <div className="text-center text-xs text-gray-500 animate-pulse py-4">Loading...</div>
                                    ) : !pmfMetrics?.pains || pmfMetrics.pains.length === 0 ? (
                                        <div className="text-xs text-gray-600 italic py-2">No pain indicator data</div>
                                    ) : pmfMetrics.pains.map((p, idx) => (
                                        <div key={idx} className="bg-[#0f0f0f] border border-gray-800 rounded-lg p-3 flex justify-between items-center text-xs">
                                            <div>
                                                <div className="font-bold text-white font-mono">{p.name}</div>
                                                <div className="text-gray-500 text-[10px] mt-0.5">{p.total} sent, {p.replies} replies</div>
                                            </div>
                                            <div className="text-right">
                                                <div className="font-bold text-[#FACC15]">{p.total ? ((p.pilots / p.total) * 100).toFixed(1) : '0.0'}%</div>
                                                <div className="text-[9px] text-gray-500">Pilot Rate</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Draft Modal */}
            {selectedDraft && (
                <div className="fixed inset-0 bg-black/85 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                    <div className="bg-[#1a1a1a] p-8 rounded-2xl shadow-2xl border border-[#FACC15]/30 max-w-2xl w-full max-h-[95vh] flex flex-col overflow-y-auto">
                        <div className="flex justify-between items-start mb-5">
                            <div><h3 className="text-white text-xl font-bold">Outreach Draft</h3>
                                <p className="text-gray-400 text-sm mt-0.5">{selectedDraft.Firm_Name}</p></div>
                            <button onClick={() => setSelectedDraft(null)} className="text-gray-400 hover:text-white text-2xl">×</button>
                        </div>
                        <div className="mb-4 flex flex-col gap-3">
                            {selectedDraft.Discovered_Managers && selectedDraft.Discovered_Managers !== "[]" && (
                                <div className="flex items-center gap-3">
                                    <label className="text-gray-400 text-sm font-bold whitespace-nowrap w-20">Manager:</label>
                                    <select className="flex-1 bg-[#0e0e0e] border border-gray-700 rounded-lg p-2.5 text-white text-sm focus:outline-none focus:border-[#FACC15]"
                                        onChange={(e) => {
                                            const name = e.target.value;
                                            if (name) {
                                                const managers = JSON.parse(selectedDraft.Discovered_Managers || "[]");
                                                const mgr = managers.find(m => m.name === name);
                                                if (mgr && mgr.email) {
                                                    setEmailInput(mgr.email);
                                                } else {
                                                    const first = name.split(' ')[0].toLowerCase().replace(/[^a-z]/g, '');
                                                    const domain = selectedDraft.Domain.replace('www.', '');
                                                    setEmailInput(`${first}@${domain}`);
                                                }
                                                try {
                                                    const newDraft = selectedDraft.Outreach_Draft.replace(/Hi [A-Za-z[\] ]+,/, `Hi ${name.split(' ')[0]},`);
                                                    setSelectedDraft({ ...selectedDraft, Outreach_Draft: newDraft });
                                                } catch { }
                                            }
                                        }}>
                                        <option value="">-- Multiple Roles Detected --</option>
                                        {JSON.parse(selectedDraft.Discovered_Managers).map((m, i) => (
                                            <option key={i} value={m.name}>{m.name} ({m.role} - {m.location})</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="flex items-center gap-3">
                                    <label className="text-gray-400 text-xs font-bold whitespace-nowrap w-16">Send Role:</label>
                                    <input type="text" value={targetRoleInput} onChange={e => setTargetRoleInput(e.target.value)}
                                        className="flex-1 bg-[#0e0e0e] border border-gray-700 rounded-lg p-2 text-white text-xs focus:outline-none focus:border-[#FACC15]"
                                        placeholder="Chief Human Resources Officer" />
                                </div>
                                <div className="flex items-center gap-3">
                                    <label className="text-gray-400 text-xs font-bold whitespace-nowrap w-16">Send To:</label>
                                    <input type="email" value={emailInput} onChange={e => setEmailInput(e.target.value)}
                                        className="flex-1 bg-[#0e0e0e] border border-gray-700 rounded-lg p-2 text-white text-xs focus:outline-none focus:border-[#FACC15]"
                                        placeholder="contact@firmname.com" />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="flex flex-col gap-1">
                                    <label className="text-gray-400 text-[10px] uppercase font-bold">Buyer Persona</label>
                                    <select value={draftBuyerPersona} onChange={e => setDraftBuyerPersona(e.target.value)}
                                        className="bg-[#0e0e0e] border border-gray-700 rounded-lg p-2 text-white text-xs focus:outline-none focus:border-[#FACC15]">
                                        <option value="UNKNOWN">UNKNOWN</option>
                                        <option value="MANAGING_PARTNER">MANAGING_PARTNER</option>
                                        <option value="OFFICE_MANAGER">OFFICE_MANAGER</option>
                                        <option value="MARKETING_MANAGER">MARKETING_MANAGER</option>
                                        <option value="CHRO">CHRO</option>
                                    </select>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label className="text-gray-400 text-[10px] uppercase font-bold">Template Hook</label>
                                    <select value={draftTemplateType} onChange={e => setDraftTemplateType(e.target.value)}
                                        className="bg-[#0e0e0e] border border-gray-700 rounded-lg p-2 text-white text-xs focus:outline-none focus:border-[#FACC15]">
                                        <option value="GENERIC">GENERIC</option>
                                        <option value="FRANKENSTEIN_EFFECT">FRANKENSTEIN_EFFECT</option>
                                        <option value="NEW_HIRE_TRIGGER">NEW_HIRE_TRIGGER</option>
                                        <option value="WEBSITE_REFRESH">WEBSITE_REFRESH</option>
                                        <option value="CREDIBILITY_LEAKAGE">CREDIBILITY_LEAKAGE</option>
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div className="flex flex-col gap-1">
                                    <label className="text-gray-400 text-[10px] uppercase font-bold">Reply Sentiment</label>
                                    <select value={draftReplyType} onChange={e => setDraftReplyType(e.target.value)}
                                        className="bg-[#0e0e0e] border border-gray-700 rounded-lg p-2 text-white text-xs focus:outline-none focus:border-[#FACC15]">
                                        <option value="NO_RESPONSE">NO_RESPONSE</option>
                                        <option value="POSITIVE">POSITIVE</option>
                                        <option value="INTERESTED">INTERESTED</option>
                                        <option value="NOT_NOW">NOT_NOW</option>
                                        <option value="NOT_A_FIT">NOT_A_FIT</option>
                                        <option value="ALREADY_HAVE_SOLUTION">ALREADY_HAVE_SOLUTION</option>
                                        <option value="REMOVE_ME">REMOVE_ME</option>
                                    </select>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label className="text-gray-400 text-[10px] uppercase font-bold">Disqualify Reason</label>
                                    <select value={draftDisqualificationReason} onChange={e => setDraftDisqualificationReason(e.target.value)}
                                        className="bg-[#0e0e0e] border border-gray-700 rounded-lg p-2 text-white text-xs focus:outline-none focus:border-[#FACC15]">
                                        <option value="NONE">NONE</option>
                                        <option value="PROCUREMENT">PROCUREMENT</option>
                                        <option value="NO_TEAM_PAGE">NO_TEAM_PAGE</option>
                                        <option value="NO_HIRING">NO_HIRING</option>
                                        <option value="PERFECT_HEADSHOTS">PERFECT_HEADSHOTS</option>
                                        <option value="NO_BUDGET">NO_BUDGET</option>
                                        <option value="NOT_DECISION_MAKER">NOT_DECISION_MAKER</option>
                                        <option value="SIZE_MISMATCH">SIZE_MISMATCH</option>
                                    </select>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label className="text-gray-400 text-[10px] uppercase font-bold">Current Solution</label>
                                    <select value={draftCurrentSolution} onChange={e => setDraftCurrentSolution(e.target.value)}
                                        className="bg-[#0e0e0e] border border-gray-700 rounded-lg p-2 text-white text-xs focus:outline-none focus:border-[#FACC15]">
                                        <option value="UNKNOWN">UNKNOWN</option>
                                        <option value="LOCAL_PHOTOGRAPHER">LOCAL_PHOTOGRAPHER</option>
                                        <option value="IN_HOUSE_PHOTOGRAPHER">IN_HOUSE_PHOTOGRAPHER</option>
                                        <option value="AI_HEADSHOT_TOOL">AI_HEADSHOT_TOOL</option>
                                        <option value="NONE">NONE</option>
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 pt-2 border-t border-gray-700/40">
                                <div className="flex flex-col gap-1">
                                    <label className="text-gray-400 text-[9px] uppercase font-bold">Pilot Invited</label>
                                    <input type="date" value={draftPilotInvited} onChange={e => setDraftPilotInvited(e.target.value)}
                                        className="bg-[#0e0e0e] border border-gray-700 rounded-lg p-1 text-white text-xs focus:outline-none focus:border-[#FACC15]" />
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label className="text-gray-400 text-[9px] uppercase font-bold">Pilot Requested</label>
                                    <input type="date" value={draftPilotRequested} onChange={e => setDraftPilotRequested(e.target.value)}
                                        className="bg-[#0e0e0e] border border-gray-700 rounded-lg p-1 text-white text-xs focus:outline-none focus:border-[#FACC15]" />
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label className="text-gray-400 text-[9px] uppercase font-bold">Pilot Started</label>
                                    <input type="date" value={draftPilotStarted} onChange={e => setDraftPilotStarted(e.target.value)}
                                        className="bg-[#0e0e0e] border border-gray-700 rounded-lg p-1 text-white text-xs focus:outline-none focus:border-[#FACC15]" />
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label className="text-gray-400 text-[9px] uppercase font-bold">Pilot Done</label>
                                    <input type="date" value={draftPilotCompleted} onChange={e => setDraftPilotCompleted(e.target.value)}
                                        className="bg-[#0e0e0e] border border-gray-700 rounded-lg p-1 text-white text-xs focus:outline-none focus:border-[#FACC15]" />
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label className="text-gray-400 text-[9px] uppercase font-bold">Paid Conv</label>
                                    <input type="date" value={draftConverted} onChange={e => setDraftConverted(e.target.value)}
                                        className="bg-[#0e0e0e] border border-gray-700 rounded-lg p-1 text-white text-xs focus:outline-none focus:border-[#FACC15]" />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
                                <div className="flex items-center gap-3">
                                    <label className="text-gray-400 text-xs font-bold whitespace-nowrap w-28">Priority Score (+):</label>
                                    <input type="number" min="0" max="10" value={draftPriorityScore} onChange={e => setDraftPriorityScore(parseInt(e.target.value) || 0)}
                                        className="w-16 bg-[#0e0e0e] border border-gray-700 rounded-lg p-1.5 text-white text-xs text-center focus:outline-none focus:border-[#FACC15]" />
                                    <span className="text-[9px] text-gray-500">(hiring, templates, Workspace)</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <label className="text-gray-400 text-xs font-bold whitespace-nowrap w-28">Friction Score (-):</label>
                                    <input type="number" min="0" max="10" value={draftFrictionScore} onChange={e => setDraftFrictionScore(parseInt(e.target.value) || 0)}
                                        className="w-16 bg-[#0e0e0e] border border-gray-700 rounded-lg p-1.5 text-white text-xs text-center focus:outline-none focus:border-[#FACC15]" />
                                    <span className="text-[9px] text-gray-500">(procurement, security, IT)</span>
                                </div>
                            </div>
                        </div>
                        {selectedDraft.Status === 'Clicked Link' ? (
                            <div className="flex-1 overflow-y-auto mb-6 bg-[#0e0e0e] p-5 rounded-xl border-2 border-green-500/50 relative min-h-[150px]">
                                <span className="absolute top-2 right-2 text-[10px] text-green-400 bg-green-900/30 px-2 py-0.5 rounded uppercase font-bold tracking-wider">⚡ Intent-Based Mode Active</span>
                                <div className="text-gray-300 text-sm leading-relaxed"
                                    dangerouslySetInnerHTML={{
                                        __html: `
                                        ${selectedDraft.Contact_Name ? `Hi ${selectedDraft.Contact_Name},` : 'Hi there,'}<br/><br/>
                                        I know how busy things get, so I wanted to make evaluating this as frictionless as possible.<br/><br/>
                                        I've gone ahead and activated a complimentary evaluation credit specifically for the ${selectedDraft.Firm_Name} team. You can use it to test the automated workflow internally and see the final headshot quality for yourself:<br/><br/>
                                        <a href="#" style="color:#60A5FA;"><b>Access Your Complimentary Credit</b></a> (requires Google Workspace)<br/><br/>
                                        Let me know what you think of the results!<br/><br/>
                                        Best,<br/><br/>
                                        Zack<br/>
                                        Founder, InstantHeadshot.pro
                                    `}} />
                            </div>
                        ) : (
                            <div className="flex-1 overflow-y-auto mb-6 bg-[#0e0e0e] p-5 rounded-xl border border-gray-700/60 focus-within:border-[#FACC15] transition-colors relative group min-h-[150px]">
                                <span className="absolute top-2 right-2 text-[10px] text-gray-500 bg-gray-900 px-2 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">Click any text to edit</span>
                                <div className="text-gray-300 text-sm leading-relaxed outline-none"
                                    contentEditable="true"
                                    suppressContentEditableWarning={true}
                                    onBlur={e => setSelectedDraft({ ...selectedDraft, Outreach_Draft: e.target.innerHTML })}
                                    dangerouslySetInnerHTML={{ __html: selectedDraft.Outreach_Draft }} />
                            </div>
                        )}
                        <div className="flex justify-between gap-3">
                            <div className="flex gap-2">
                                <button onClick={handleSendEmail} disabled={sending}
                                    className={`font-bold py-2.5 px-4 rounded-lg transition flex items-center gap-2 text-sm
                                        ${sending ? 'bg-gray-600 cursor-not-allowed text-gray-300' : ['Emailed', 'Opened', 'Clicked Link', 'Value Bump Sent', 'Follow-Up Clicked'].includes(selectedDraft.Status) ? 'bg-red-600 hover:bg-red-500 text-white ring-2 ring-red-400' : 'bg-blue-600 hover:bg-blue-500 text-white'}`}>
                                    {sending
                                        ? <><span className="animate-spin inline-block">⌛</span> Processing...</>
                                        : <><span>{['Emailed', 'Opened', 'Clicked Link', 'Value Bump Sent', 'Follow-Up Clicked'].includes(selectedDraft.Status) ? 'Resend 1st Touch' : 'Send via Zoho SMTP'}</span><svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg></>}
                                </button>
                                {['Emailed', 'Opened', 'Clicked Link'].includes(selectedDraft.Status) && (
                                    <button onClick={handleSendFollowUp} disabled={sending}
                                        className={`font-bold py-2.5 px-4 rounded-lg transition flex items-center gap-2 text-sm border-2 border-green-500 bg-green-500/20 hover:bg-green-500/40 text-green-400`}>
                                        {sending ? '⌛' : '🚀'} Send Value Bump
                                    </button>
                                )}
                            </div>
                            <div className="flex gap-3">
                                <button onClick={handleSaveDraft} disabled={sending}
                                    className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-2.5 px-4 rounded-lg transition text-sm flex items-center gap-2">
                                    💾 Save Info
                                </button>
                                <button onClick={() => { navigator.clipboard.writeText(selectedDraft.Outreach_Draft); showToast('Draft copied!'); }}
                                    className="bg-[#FACC15] hover:bg-yellow-400 text-black font-bold py-2.5 px-5 rounded-lg transition text-sm">Copy HTML Draft</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        </div>
    );
};

export default GrowthDashboard;
